import React, { useState, useEffect, version, useMemo, useCallback, useRef, memo } from "react";
import { Search, useLocation, useNavigate } from 'react-router-dom';
import axios, { all, AxiosError } from "axios";
import { pdfjs } from 'react-pdf';
import SettingsIcon from '@mui/icons-material/Settings';

import { 
    Grid, IconButton, Box, 
    Tabs, Tab, Container, 
    Card, Typography, Toolbar,
    Tooltip,
    accordionActionsClasses,
    CardHeader,
    CardContent,
    fabClasses,
    Input
  } from '@mui/material';
import SavePlotModal from "./SavePlotModal";
import AddPlotGroupModal from "./AddPlotGroupModal";
import AddSamplesModal from "./AddSamplesModal";
import ConfirmationDialog from './ConfirmationDialog';
import { Layout } from 'react-grid-layout';
import MenuIcon from '@mui/icons-material/Menu';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import SortableCard from "./SortableCard";
import SampleGroupModal from "../shared/components/SampleGroupModal";
import { useDropdowns, useLoadDataframe, useLoadDataframeV2, useTableItems, usePlotGroups, 
         useMultiPlot, useGeneratePlots, useDeepCompareMemo, usePartitions, usePathways } from '../shared/hooks/useResultsHooks';

import './Results.css';
import PlotGrid from "./PlotGrid";
import PlotControls from "../shared/components/PlotControls";
import SearchableDropdown from "../shared/components/SearchableDropdown";
import FilterDrawer from "../shared/components/FilterDrawer";
import BrowseTab from "./BrowseTab";
import PlotOptionsModal from '../shared/components/PlotOptionsModal';
import AddPartitionModal from "./AddPartitionModal";
import { filter, get, isEqual, last, memoize, set, update } from 'lodash';
import { deepEqual, getAxisFiltersFromSelectedItems, getFilterKeys, getIndexMapOfStrArray, isObject, usePersistedState, useQueryPersistedState } from '../shared/utilities/utils';
import _default from "@mui/material/styles/identifier";
import { AddCircleOutline, CallMerge, CreateNewFolder, PlaylistRemove, Splitscreen } from "@mui/icons-material";
import Assistant from './Assistant';
import { defaultColorMap, stringToColorDefault } from '../shared/utilities/Colors';
import GroupEditorModal from "../shared/components/GroupEditorModal";
import AddFromGroupModal from "./AddFromGroupModal";
import RenameDialog from "./RenameDialog";
import _ from "lodash";
import NumericFilterInput from "../shared/components/NumericFilterInput";


pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();


function getCookie(cname: any) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

const defaultSymbolMap: { [key: string]: string } = {
  0: 'circle',
  1: 'square',
  2: 'diamond',
  3: 'cross',
  4: 'x',
  5: 'triangle-up',
  6: 'triangle-down',
  7: 'triangle-left',
  8: 'triangle-right',
  9: 'triangle-ne',
  10: 'triangle-se',
  11: 'triangle-sw',
  12: 'triangle-nw',
  13: 'pentagon',
  14: 'hexagon',
  15: 'hexagon2',
  16: 'octagon',
  17: 'star',
  18: 'hexagram',
  19: 'star-triangle-up',
  20: 'star-triangle-down',
  21: 'star-square',
  22: 'star-diamond',
  23: 'diamond-tall',
  24: 'diamond-wide',
  25: 'hourglass',
  26: 'bowtie',
  27: 'circle-cross',
  28: 'circle-x',
};

//// TYPES ////
type ContentType = 'Text' | 'Title' | 'CustomPlot';

type Thumbnail = {
  src: string;
  alt: string;
  page: number;
}

type PageData = {
  [page: number]: {
    layout: Layout[];
    content_ids: number[];
  };
}
type BoardData = {
  pageData: PageData
  title: string;
}

type unsavedContent = {
  [contentId: number]: {
    content: string | number[] | {width: number | undefined, height: number | undefined};
  };
}

interface AxisOptionsParams {
  study: string[];
  sample: string[];
  treatment: string[];
  timepoint: string[];
  analysisType: string[];
}
////////////////

const getLayoutData = async (boardId: number): Promise<BoardData> => {
  try {
    let boardId = 1;
    const response = await axios.get("/tumors/api/v1/get_analysis_board_layout/" + boardId);
    return response.data;
  } catch (err) {
    const serverError = err as AxiosError;
    if (serverError && serverError.response) {
      if (serverError.response.status === 403) {
        window.location.href = '/accounts/login/?next=' + encodeURIComponent(window.location.pathname + window.location.search);
      }
    }
  }
  return {pageData: {}, title: ''};  // return an empty object if there's an error
}

const initialPlotConfig : PlotConfig = {
  plotType: 'scatter',
  fixedColorMode: false,
  fixedColor: '#4287f5',
  plot_bgcolor: '#ffffff',// '#e5ecf6'
  xaxis_showgrid: false,
  yaxis_showgrid: true,
  gridline_color: '#dbe2ec',
  xaxis_showline: true,
  yaxis_showline: true,
  axis_linecolor: '#dbe2ec',//'grey',
  colorMap: defaultColorMap,
  colorscale: 'Default',
  colorscaleTitle: undefined,
  symbolMap: defaultSymbolMap,
  fillMap: {},
  title: '',
  xtitle: '',
  xaxis_autotitle: true,
  ytitle: '',
  yaxis_autotitle: true,
  xmin: null,
  xmax: null,
  ymin: null,
  ymax: null,
  zmin: null,
  zmax: null,
  xmin_rel: null,
  xmax_rel: null,
  ymin_rel: null,
  ymax_rel: null,
  zmin_rel: null,
  zmax_rel: null,
  zmin_log2: null,
  zmax_log2: null,
  fontSize: 16,
  titleFontSize: 20,
  xAxisTitleFontSize: undefined,
  xAxisLabelFontSize: undefined,
  yAxisTitleFontSize: undefined,
  yAxisLabelFontSize: undefined,
  boxpoints: 'all',
  boxMeanLine: true,
  errorBars: true,
  showLegend: true,
  showTitle: true,
  markerSize: 6,
  xaxis_type: 'lin',
  yaxis_type: 'lin',
  pvalue: undefined,
  pvalueColumn: undefined,
  pvalueControl: undefined,
  pvalueMethod: 'ttest',
  pvalueHideInsignificant: false,
  pvalueDisplayMode: 'lines+text',
  pvalueLegend: false,
  pvalueUseRaw: false,
  scatterColorBy: undefined,
  scatterLabelBy: undefined,
  scatterShowLabels: false,
  subplotXgap: 0.05,
  subplotYgap: 0.05,
  showTileTitles: true,
  showXtickLabels: true,
  showYtickLabels: true,
  heatmapXcluster: false,
  heatmapYcluster: false,
  heatmapLog2: false,
  heatmapHandleFiltered: 'keepRows',
  bargap: 0.2,
  barMode: 'none',
  barGroupBy: '',
  colorOverride: {},
  annotations: [],
  shapes: [],
  showKWResult: true,
  tileMode: 'autoGrid',
};

const initialConfigurations = {
  plot1: { ...initialPlotConfig },
};

const Results = () => {
  const [savePlotModalOpen, setSavePlotModalOpen] = useState(false);
  const [addPlotGroupModalOpen, setAddPlotGroupModalOpen] = useState(false);
  const [addSamplesModalOpen, setAddSamplesModalOpen] = useState(false);
  const [addGeneModelOpenNano, setAddGeneModelOpenNano] = useState(false);
  const [addGeneModelOpenSeq, setAddGeneModelOpenSeq] = useState(false);
  const [confirmDeletePlotGroupOpen, setConfirmDeletePlotGroupOpen] = useState(false);
  const [confirmDeletePartitionOpen, setConfirmDeletePartitionOpen] = useState(false);
  const [confirmDeletePlotOpen, setConfirmDeletePlotOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [renameTargetId, setRenameTargetId] = useState<number | null>(null);
  const [dataProcessLoading, setIsDataProcessLoading] = useState(false);
  const [sample, setSample] = useState<SearchableDropdownOption[]>([]);
  const [indexSortOrder, setIndexSortOrder] = useState<string[]>([]);

  const selectedTab = 1

  // Use persisted state maintains the value of the state in session storage, and sets the stored session value as the default, if it exists
  const [versionTypeOptions, setVersionTypeOptions] = usePersistedState<SearchableDropdownOption[]>('versionTypeOptions', []);
  const [partitionOptions, setPartitionOptions] = usePersistedState<SearchableDropdownOption[]>('partitionOptions', []);
  const [accountOptions, setAccountOptions] = usePersistedState<SearchableDropdownOption[]>('accountOptions', []);
  const [sampleGroupOptions, setSampleGroupOptions] = usePersistedState<SearchableDropdownOption[]>('sampleGroupOptions', []);
  const [customFilterOptions, setCustomFilterOptions] = usePersistedState<CustomFilterOptions>('customFilterOptions', {});
  const [partitionToSampleGroupDict, setPartitionToSampleGroupDict] = usePersistedState<Record<number, number[]>>('partitionToSampleGroupDict', {});
  const [sampleGroupToSampleDict, setSampleGroupToSampleDict] = usePersistedState<Record<number, number[]>>('sampleGroupToSampleDict', {});
  const [sampleGroupDict, setSampleGroupDict] = usePersistedState<Record<number, string>>('sampleGroupDict', {});
  const [isCompareMode, setIsCompareMode] = usePersistedState<boolean>('isCompareMode', false);
  const [tileState, setTileState] = usePersistedState<TileState>('tileState', {});   // TO DO persisted key should be tied to a specific plot ID
  const [currentPlotName, setCurrentPlotName] = usePersistedState<string>('currentPlotName', '');
  const [tileSharedY, setTileSharedY] = usePersistedState<boolean>('tileSharedY', false);
  const [tileSharedX, setTileSharedX] = usePersistedState<boolean>('tileSharedX', true);
  

  // These state variables are initialized to the cache in local storage and tied to a key specific for the URL query parameters
  const [study, setStudy] = useQueryPersistedState<SearchableDropdownOption[]>('study', []);
  const [partition, setPartition] = useQueryPersistedState<SearchableDropdownOption[]>('partition', []);
  const [account, setAccount] = useQueryPersistedState<SearchableDropdownOption[]>('account', []);
  const [sampleGroup, setSampleGroup] = useQueryPersistedState<SearchableDropdownOption[]>('sampleGroup', []);
  const [treatmentOptions, setTreatmentOptions] = useQueryPersistedState<SearchableDropdownOption[]>('treatmentOptions',[]);
  const [treatment, setTreatment] = useQueryPersistedState<SearchableDropdownOption[]>('treatment',[]);
  const [timepointOptions, setTimepointOptions] = useQueryPersistedState<SearchableDropdownOption[]>('timepointOptions',[]);
  const [timepoint, setTimepoint] = useQueryPersistedState<SearchableDropdownOption[]>('timepoint',[]);

  const [selectedPlotTab, setSelectedPlotTab] = useState<number>(0);
  const [sampleGroupColorDict, setSampleGroupColorDict] = useState<Record<number, string>>({})
  const [partitionSampleOptions, setPartitionSampleOptions] = useState<SearchableDropdownOption[]>([]);
  const [partitionDict, setPartitionDict] = useState<Record<number, string>>({})
  const [tempSample, setTempSample] = useState<SearchableDropdownOption[]>([]);
  const [versionType, setVersionType] = useState<SearchableDropdownOption[]>([]);
  const [currentPage, setCurrentPage] = useState(0); 
  const [unstagedData, setUnstagedData] = useState<PageData>({});
  const [contentToSave, setContentToSave] = useState<unsavedContent>({});
  const [boardId, setBoardId] = useState(1); 
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [tumorDict, setTumorDict] = useState<Dictionary>({});
  const [studyDict, setStudyDict] = useState<Dictionary>({});
  const [isSuperuser, setIsSuperuser] = useState<boolean>(false);
  const tableItems = useTableItems<AxisItem>();
  const advancedSortingState = useTableItems<SortItem>();
  const [basicSortingState, setBasicSortingState] = useState<SortState>({});
  const [numTileColumns, setNumTileColumns] = useState(2);
  const [dataManipulation, setDataManipulation] = useState<number | null>(null);
  const [filtersState, setFiltersState] = useState<OldFilterState>({});
  const [viewableAxisFiltersState, setViewableAxisFiltersState] = useState<Set<string>>(new Set());
  const [axisFiltersState, setAxisFiltersState] = useState<FilterState>({});
  const [filteredPlotData, setFilteredPlotData] = useState<DataRow[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [fetchedObjectsChanged, setFetchedObjectsChanged] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const initialLoadingMessage = 'Loading...';
  const [loadingMessage, setLoadingMessage] = useState<string>(initialLoadingMessage);
  const [tooManyDataPointsError, setTooManyDataPointsError] = useState(false)
  const [plotLayersFinal, setPlotLayersFinal] = useState<any>([])
  const [plotLayoutFinal, setPlotLayoutFinal] = useState<any>({})
  const [plotError, setPlotError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [groupedSampleMode, setGroupedeSampleMode] = useState<boolean>(false)
  const [currentPlotGroup, setCurrentPlotGroup] = useState<PlotGroup | null>(null)
  const [plotOptionsModalOpen, setPlotOptionsModalOpen] = useState(false);
  const [partitionModalOpen, setPartitionModalOpen] = useState(false);
  const [sampleGroupModalOpen, setSampleGroupModalOpen] = useState(false)
  const [pathwayEditModalOpenNano, setPathwayEditModalOpenNano] = useState(false)
  const [pathwayEditModalOpenSeq, setPathwayEditModalOpenSeq] = useState(false)
  const [defaultOpenPathwayIdNano, setDefaultOpenPathwayIdNano] = useState<number | undefined>(undefined)
  const [defaultOpenPathwayIdSeq, setDefaultOpenPathwayIdSeq] = useState<number | undefined>(undefined)
  const [pathwayGroupAddModalVersionType, setPathwayGroupAddModalVersionType] = useState<string | undefined>(undefined)
  const [applySampleDisabled, setApplySampleDisabled] = useState(true)
  const [showTile, setShowTile] = useState(false)
  const prevFetchedSelectedItemsRef = useRef<SelectedItems | null>(null);
  const previousSampleGroupRef = useRef<SearchableDropdownOption[]>([]);
  const previousSampleGroupToSampleDictRef = useRef<Record<string, number[]>>({});
  const prevSampleGroupColorDictRef = useRef<Record<number, string>>({});
  const prevTempSampleRef = useRef<any[]>([]);
  const prevSampleRef = useRef<any[]>([]);

  const [plotConfigs, setPlotConfigs] = useState<{ [key: string]: PlotConfig }>(initialConfigurations);
  const [configChanged, setConfigChanged] = useState(false);
  const [plotConfigChanged, setPlotConfigChanged] = useState(false);
  const [selectedItemsChanged, setSelectedItemsChanged] = useState(false);
  const [plotTypeDict, setPlotTypeDict] = useState<Dictionary>({});
  const [addPlot, setAddPlot] = useState(false);
  const sampleLimit = 100
  const [refresh, setRefresh] = useState(0);
  const [sampleErrorText, setSampleErrorText] = useState<String | null>(null);
  const [sampleError, setSampleError] = useState<boolean>(false);
  const [isHorizontalLayout, setIsHorizontalLayout] = useState<boolean>(true);
  const [filteredAnalyses, setFilteredAnalyses] = useState<Analysis[]>([]);
  const [imageViewerOpen, setImageViewerOpen] = useState<boolean>(false);

  const tileModeOn = useDeepCompareMemo(() => (Object.values(tileState).some((value) => value === true)), [tileState]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [colorOverrideMap, setColorOverrideMap] = useState<Record<string, string>>({});

  
  const sampleLoadedFromUrl = useRef(false);
  useEffect(() => {
    
    if (tempSample.length == 0 && sample.length == 0 && sampleLoadedFromUrl.current) {
      // Case where no sample is selected (i.e. initial load of component)
      setApplySampleDisabled(true);
      setSampleErrorText('Select at least one sample to analyze.');
      setSampleError(true);
    } else if (!isEqual(tempSample, sample)) {
      // Case where user has not hit the Apply button yet
      setApplySampleDisabled(false);
      // setSampleErrorText('Click Apply');
    } else {
      setApplySampleDisabled(true);
      setSampleError(false);
    }
  }, [tempSample, sample]);  

  useEffect(() => {
    // Check if sampleGroupColorDict has actually changed
    const prevColorDict = prevSampleGroupColorDictRef.current;
    const colorDictChanged = JSON.stringify(prevColorDict) !== JSON.stringify(sampleGroupColorDict);
  
    // Update the ref with the current value for the next render
    prevSampleGroupColorDictRef.current = JSON.parse(JSON.stringify(sampleGroupColorDict));
  
    if (colorDictChanged) {
      setApplySampleDisabled(false);
    }

  }, [sampleGroupColorDict])
  
  const selectedSamples = useDeepCompareMemo(() => {
    if (!sample) return [];
    let newSamples = sample.filter(s => s.id !== undefined).map(s => s.id!);
    if (newSamples.length > sampleLimit) {
      // newSamples = newSamples.slice(0, sampleLimit)
    }
    return newSamples;
  }, [sample]);

  const selectedVersionTypes = useDeepCompareMemo(() => {
    if (!versionType) return [];
    return versionType.map(v => v.id!);
  }, [versionType]);

  const { 
    versionTypeDict,
    timepointDict, 
    treatmentDict,  
    dataPipelineDict,
    versionTypes,
  } = useDropdowns(selectedSamples, refresh);

  useEffect(() => {
    const treatmentNameToColor = Object.keys(treatmentDict).reduce((acc, key) => {
      const treatment = treatmentDict[Number(key)];
      if (treatment.color !== undefined && treatment.color !== null) {
        acc[treatment.name] = treatment.color;
      }
      return acc;
    }, {} as Record<string, string>);

    setColorOverrideMap(treatmentNameToColor);

  }, [treatmentDict]);

  const {
    pathways: pathwayDict,
    loading:pathwaysIsLoading,
    error: pathwaysError,
    refetch: fetchPathways,
   } = usePathways();

  const [pathways, setPathways] = useState<SearchableDropdownOption[]>([])

  const availableVersionTypes = useDeepCompareMemo(() => {
    return [...Object.keys(versionTypeDict).sort()].map(key => Number(key));
  }, [versionTypeDict]);

  const location = useLocation();
  const navigate = useNavigate();

  const [selectedPlotIds, _setSelectedPlotIds] = usePersistedState<number[]>("selectedPlotIds", [])

  useEffect(() => {
    // ensure that we set the plots as loading from initial state
    setLoadingPlots(selectedPlotIds)
  }, []);

  const setSelectedPlotIds = (plotIds : number[]) => {
    return _setSelectedPlotIds(prev => {
      const lastSelected = plotIds.filter(x => !prev.includes(x))

      if (lastSelected.length && plotIds.length >= prev.length){ //
        setLoadingPlots(prevF => [...lastSelected, ...prevF])
      }
      return plotIds
    })
  }

  const [selectedMultiPlot, setSelectedMultiPlot] = useState<number>(selectedPlotIds.length > 0 ? selectedPlotIds[0] : 0);


  // useEffect(()=>{
  //   console.log("Mutli plot id", selectedMultiPlot)
  // },[selectedMultiPlot])

  useEffect(()=>{
    // console.log("new selected plot ids", selectedPlotIds)
    if (selectedMultiPlot === 0 || !selectedPlotIds.includes(selectedMultiPlot)){
      setSelectedMultiPlot(selectedPlotIds[0])
    }
    // Every time selectedPlotIds changes, set tileState back to empty (forces tileModeOn to false)
    setTileState({});

  }, [selectedPlotIds])

  const updateSampleGroups = (data: any) => {

    if (data.sample_groups.length > 0) {

      let partitionId = data.id
      let newPartitionToSampleDict = {...partitionToSampleGroupDict}
      let newSampleDictToSampleDict = {...sampleGroupToSampleDict}
      let newSampleGroupDict = {...sampleGroupDict}
      let newSampleGroupColorDict = {...sampleGroupColorDict}
      newPartitionToSampleDict[partitionId] = []

      let newSampleGroupOptions: SearchableDropdownOption[] = []
      for (let samplegroup of data.sample_groups) {
        let sampleGroupOption = {id: samplegroup.id, label: samplegroup.name, value: samplegroup.name}
        newSampleGroupOptions.push(sampleGroupOption)
        newPartitionToSampleDict[partitionId].push(samplegroup.id)
        newSampleDictToSampleDict[samplegroup.id] = samplegroup.tumor_ids
        newSampleGroupDict[samplegroup.id] = samplegroup.name
        newSampleGroupColorDict[samplegroup.id] = samplegroup.color || stringToColorDefault(samplegroup.id as string)
      }

      setSampleGroupOptions(newSampleGroupOptions)
      setSampleGroup(newSampleGroupOptions)
      setPartitionToSampleGroupDict(newPartitionToSampleDict)
      setSampleGroupToSampleDict(newSampleDictToSampleDict)
      setSampleGroupDict(newSampleGroupDict)
      setSampleGroupColorDict(newSampleGroupColorDict)
    }
  }

  const fetchUserOptions = async (data?: any) => {
    try {
      const response = await axios.get("/tumors/api/v1/load_user_options/");
      
      setSampleGroupColorDict(response.data.sample_group_colors);
      setTumorDict(response.data.tumors);
      setStudyDict(response.data.studies);
      setPlotTypeDict(response.data.plot_types);
      setPartitionDict(response.data.partitions);
      setSampleGroupDict(response.data.sample_groups);
      setPartitionToSampleGroupDict(response.data.partition_to_sample_groups);
      setSampleGroupToSampleDict(response.data.sample_group_to_samples);
      setUserRoles(response.data.user_groups);
      let newAccountOptions: SearchableDropdownOption[] = [];
      if (response.data.accounts) {
        newAccountOptions = Object.entries(response.data.accounts).map(([key, value]) => {
          return { id: parseInt(key) as number, label: value as string, value: value as string } as SearchableDropdownOption;
        });
      }
      setAccountOptions(newAccountOptions);
      
      if (response.data.is_superuser) {
        setIsSuperuser(response.data.is_superuser);
      }
      
      if (data) {
        let newPartitionName = studyDict[data.study] + ' - ' + data.name;
        let newPartitionOption = {id: data.id, label: newPartitionName, value: newPartitionName};
        setPartitionOptions(prevOptions => [...prevOptions, newPartitionOption]);
        setPartition([newPartitionOption]);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          const currentSearchParams = new URLSearchParams(window.location.search);
          const readableUrl = `${window.location.pathname}?${currentSearchParams.toString()}`.replace(/%2C/g, ',');
          window.location.href = '/accounts/login/?next=' + readableUrl;
        } else {
          console.error('Axios error:', error.message);
        }
      } else {
        console.error('Unexpected error:', error);
      }
    }
  };

  // const { data: dataPipelines, isError: dataPipelinesIsError, error: dataPipelinesError} = useDataPipelines(sample.map(s => s.id!));
  const { plotGroups, error: plotGroupsError, refetch: refetchPlotGroups, add: addNewPlotGroup, delete: deletePlotGroup, flatPlotDict: flatPlotDict } = usePlotGroups(availableVersionTypes);
  const { add: addNewPartition, delete: deletePartition, update: updatePartition } = usePartitions(fetchUserOptions, updateSampleGroups);
  const multiPlotData = useMultiPlot(selectedPlotIds, selectedMultiPlot, plotTypeDict, initialPlotConfig, setDataManipulation);

  const memoizedMultiPlotData = useMemo(() => multiPlotData, [JSON.stringify(multiPlotData)]);

  useEffect(() => {
    if (Object.keys(multiPlotData).length > 1) {
      setShowTile(false)
    } else {
      setShowTile(true)
    }
  }, [memoizedMultiPlotData])

  const [loadingPlots, setLoadingPlots] = useState<number[]>([]);
  const [editedMultiPlotData, setEditedMultiPlotData] = useState<MultIPlotData>({})

  const getVersionTypesFromMultiPlot = useCallback((multiPlotData: MultIPlotData ): number[] => {
    let allVersionTypes: number[] = []
    let allPipelineVersionTypes: number[] = []
    for (let plot in multiPlotData) {
      if (!selectedPlotIds.includes(parseInt(plot))){
        continue
      }
      allVersionTypes = [...multiPlotData[plot].selectedVersionTypes, ...allVersionTypes]
      for (let pipeline in multiPlotData[plot].dataPipelines) {
        if (pipeline in dataPipelineDict) {
          allPipelineVersionTypes = [...dataPipelineDict[pipeline]['version_types'], ...allPipelineVersionTypes]
        }
      }
    }
    const versionTypeList: number[] = [...new Set<number>([...allVersionTypes, ...allPipelineVersionTypes])]
    return versionTypeList
  }, [selectedPlotIds, dataPipelineDict]);

  const memoizedEditedMultiPlotData = useMemo(()=> editedMultiPlotData, [JSON.stringify(editedMultiPlotData)])

  const checkIsPathwayModeFromSelectedItems = (selectedItems: SelectedItems) => {
    if (selectedItems === undefined) {
      return false;
    }
    const currentSelectedItems = selectedItems;
    let isPathwayMode = false;
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] && currentSelectedItems[axis][0].method === 'data' &&currentSelectedItems[axis][0].pathwayMode) {
        isPathwayMode = true;
      }
    }
    return isPathwayMode
  };

  

  const selVTRef = useRef<number[]>([]);

  const updateMultiPlotVersionTypes = (vts: number[], plotData: MultIPlotData, pastVts: number[]) => {
    
    const removedVTs = pastVts.filter(item => !vts.includes(item))
    const addedVTs = vts.filter(item => !pastVts.includes(item)); 
    // update version types from edited multiplot data
    // prune any multi plot data that doesn't have its version type
    const newVersionTypes: number[] = [];
    // const plotDataCopy = structuredClone(editedMultiPlotData);
    for (let plot of Object.keys(plotData)){
      const selectedItems = structuredClone(plotData[parseInt(plot)].selectedItems)
      for (let key in selectedItems){
        const selectedItemsToClear = []
        let index = 0;
        for (let item of selectedItems[key]) {
          // console.log(selectedPlotItems, item, key)
          if (item && item.versionType) {
            // console.log("selected version types", selectedVersionTypes)
            if (!vts.includes(item.versionType) && removedVTs.includes(item.versionType)){
              selectedItemsToClear.push(index)
            }
            else {
              newVersionTypes.push(item.versionType);
            }
          }
          index += 1
        }
        for (let i of selectedItemsToClear){
          selectedItems[key].splice(i, 1)
        }
      }
      plotData[parseInt(plot)].selectedItems = selectedItems
      const filteredAddedVts = addedVTs.filter(item => !newVersionTypes.includes(item));
      plotData[parseInt(plot)].selectedVersionTypes = newVersionTypes.concat(filteredAddedVts);
    }

    return plotData;

    
  }

  const accountFilter = useMemo(() => {
    return account.map(a => a.id!).filter(a => a !== undefined) as number[];
  }, [account]);


  const geneFilter: string[] = useMemo(() => {
    const out: string[] = []
    Object.keys(axisFiltersState).forEach((key) => {
      const filter = axisFiltersState[parseInt(key)]
      if (filter.type === 'categorical' && 'Gene' && (filter.versionType === 'RNA 1.0' || filter.versionType === 'RNA-Seq 1.0') && filter.value.length > 0) {
        out.push(...filter.value.map((item: SearchableDropdownOption) => item.value));
      }
    });
    return out
  }, [axisFiltersState]);

  const { 
    data: loadedData = {data: [], columns: {}, categoricalColumns: {}, numericColumns: {}} as Data,
    isError: isDataError = false, 
    error: dataErrorMessage = '', 
    progress: dfProgress = 0,
    taskCompleted = true,
    message: dfMessage = 'Loading Initiated',
    isLoading: dataFrameIsLoading = false,
  } = useLoadDataframeV2(selectedSamples, selectedVersionTypes, treatment, timepoint, dataManipulation, accountFilter, refresh, geneFilter);


  useEffect(()=>{
    if (selVTRef.current === undefined && selectedVersionTypes !== undefined){
      selVTRef.current = selectedVersionTypes;
    }
    if (Object.keys(memoizedMultiPlotData).length > 0) {
      // console.log("Memoized multi plot data", memoizedMultiPlotData)
      // if (Object.keys(tableItems.selectedItems).length > 0 && plotConfigs) {
      if ((Object.keys(tableItems.selectedItems).length > 0 && plotConfigs.plot1 && axisFiltersState && taskCompleted)){
        // const idsToSkip = Object.keys(editedMultiPlotData).filter((key:string) => {
        //   const numkey:number = parseInt(key) as number
        //   return editedMultiPlotData[numkey].edited !== undefined && editedMultiPlotData[numkey].edited
        // })
        // if (selectedVersionTypes === undefined) {
        //   return;
        // }
        
        const unsavedData = Object.fromEntries(
          Object.entries(memoizedEditedMultiPlotData)
            .filter(([pid, pdata]) => {
              return pdata.edited !== undefined && pdata.edited && selectedPlotIds.includes(parseInt(pid))
            })
        );

        const dataCopyMultiFiltered = Object.fromEntries(
          Object.entries(
            memoizedMultiPlotData
          ).filter(([pid, pdata]) => (!(pid in unsavedData) && selectedPlotIds.includes(parseInt(pid)) && pdata.selectedItems !== null))
        );

        let newData: MultIPlotData = Object.assign({},dataCopyMultiFiltered,unsavedData);
              
        // console.log(loadingPlots, selectedMultiPlot, newData)
        if (selectedMultiPlot in newData){
          if (loadingPlots.includes(selectedMultiPlot)){
            setEditedMultiPlotData(newData); 
          }
          else {
            const updatedEntry = {
              ...newData[selectedMultiPlot],
            }

            const prevSelectedItems = structuredClone(updatedEntry.selectedItems)
            
            if (plotConfigs){  
              if (plotConfigs.plot1.colorOverride !== undefined){
                for (let key in colorOverrideMap){
                  if (!(key in plotConfigs.plot1.colorOverride)){
                    plotConfigs.plot1.colorOverride[key] = colorOverrideMap[key];
                  }
                }
              }
              updatedEntry.configuration = plotConfigs.plot1
            }
            if (Object.keys(tableItems.selectedItems).length > 0) {
              updatedEntry.selectedItems = structuredClone(tableItems.selectedItems) as SelectedItems
            }
            if (axisFiltersState){
              updatedEntry.axisFiltersState = structuredClone(axisFiltersState)
            }
            if (pathways && updatedEntry.selectedItems !== null  ){ //&& checkIsPathwayModeFromSelectedItems(updatedEntry.selectedItems)
              updatedEntry.configuration.pathways = structuredClone(pathways)
            }
            if (tileState){
              updatedEntry.tileState = structuredClone(tileState)
            }

            newData[selectedMultiPlot] = updatedEntry
            
            newData = updateMultiPlotVersionTypes(selectedVersionTypes, newData, selVTRef.current)
            const isEqual = deepEqual(newData[selectedMultiPlot], memoizedMultiPlotData[selectedMultiPlot]);


            if (!isEqual || (loadingPlots.length > 0 && loadingPlots.every(e => e in newData))){
              if (!isEqual){
                newData[selectedMultiPlot].edited = true;
                if (newData[selectedMultiPlot].selectedItems !== null && prevSelectedItems !== null){
                  if (newData[selectedMultiPlot].selectedItems?.['Y-Axis Data'][0]?.method === 'data' && !deepEqual(newData[selectedMultiPlot].selectedItems?.['Y-Axis Data'], prevSelectedItems?.['Y-Axis Data'])){
                    newData[selectedMultiPlot].configuration.yaxis_autotitle = true;
                  }
                  if (newData[selectedMultiPlot].selectedItems?.['X-Axis Data'][0]?.method === 'data' && !deepEqual(newData[selectedMultiPlot].selectedItems?.['X-Axis Data'], prevSelectedItems?.['X-Axis Data'])){
                    newData[selectedMultiPlot].configuration.xaxis_autotitle = true;
                  }
                }
                else if (newData[selectedMultiPlot].selectedItems !== null && prevSelectedItems === null) {
                  newData[selectedMultiPlot].configuration.yaxis_autotitle = true;
                  newData[selectedMultiPlot].configuration.xaxis_autotitle = true;
                }
              }
              // if (loadingPlots.length > 0) {
              setEditedMultiPlotData(newData)
              //  }
              // else {
              //   setEditedMultiPlotData(updateMultiPlotVersionTypes(selectedVersionTypes, newData, selVTRef.current));
              // }
            }
          }
        }
        else if (loadingPlots.length > 0 && loadingPlots.every(e => e in newData)){
          setEditedMultiPlotData(newData)
        }
        
      }
      else {
        setEditedMultiPlotData(memoizedMultiPlotData)
      }
    }
    selVTRef.current = selectedVersionTypes; 
  }, [memoizedMultiPlotData, tableItems.selectedItems, plotConfigs.plot1, axisFiltersState, pathways, tileState])


  const {
    selectedItems: fetchedSelectedItems,
    selectedVersionTypes: fetchedVersionTypes,
    axisFiltersState: fetchedAxisFilter,
    error: customPlotError,
    configuration: fetchedConfiguration,
    tileState: fetchedTileState,
    saveCustomPlot,
    deleteCustomPlot,
    isLoading: isCustomPlotLoading = true,
  } = useMemo(() => {
    // console.log("Memoized edited multi plot data", memoizedEditedMultiPlotData)
    return loadingPlots.length === 0 && memoizedEditedMultiPlotData[selectedMultiPlot] !== undefined ? memoizedEditedMultiPlotData[selectedMultiPlot] : 
    {
      selectedItems:{} as SelectedItems, 
      selectedVersionTypes:[],
      axisFiltersState:{} as FilterState, 
      error: '',
      configuration: {} as PlotConfig,
      tileState: {} as TileState,
      saveCustomPlot: () => {},
      deleteCustomPlot: () => {},
      isLoading: true,
    }
  }, [memoizedEditedMultiPlotData, selectedMultiPlot, loadingPlots]);

  const disableSave = selectedPlotIds.length && flatPlotDict[selectedMultiPlot] ? (flatPlotDict[selectedMultiPlot].public && !isSuperuser) : false

  useEffect(()=>{
    if (selectedPlotIds.length === 0){
      // const saved = sessionStorage.getItem("selectedPlotId");
      // if (saved){
      //   setSelectedPlotId(JSON.parse(saved) as number)
      // }
      // else {
      if (plotGroups.length > 0){
        for (let groupIndex in plotGroups){
          const groupPlots = plotGroups[groupIndex].plots
          if (groupPlots.length > 0){
            const newPlotID = groupPlots[0].id;
            setSelectedPlotIds([newPlotID]);
            break;
          }
        }
        
      }

    }
  }, [plotGroups, selectedPlotIds])

  useEffect(()=> {
    if (memoizedEditedMultiPlotData[selectedMultiPlot]?.configuration?.name){
      const _name = memoizedEditedMultiPlotData[selectedMultiPlot].configuration.name

      setCurrentPlotName(_name !== undefined ? _name : '')
    }   
  }, [selectedMultiPlot, memoizedEditedMultiPlotData])

  const handleSetPlotConfigs = (configs: Record<string, PlotConfig>, save: boolean) => {
    const plotId = Object.keys(configs)[0]
    if (plotId === undefined) {
      return;
    }

    updatePlotConfigWithHeatmapProtection(plotId, configs[plotId]);
    if (save && (!disableSave) ) {
      setConfigChanged(true);
    }
  };

  const handleDeleteCurrentPartition = () => {
    if (partition.length > 0) {
      let partitionId = partition[0].id
      if (partitionId !== undefined)
        deletePartition(partitionId)
      setPartition([])
    }
    setConfirmDeletePartitionOpen(false);
  }

  useEffect(() => {
    if (configChanged) {
      handleSavePlot();
      setConfigChanged(false);
    }
  }, [plotConfigs, configChanged]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedPlotTab(newValue);
  };

  const updatePlotConfig = (plotId: any, config: any) => {
    setPlotConfigs((prevConfigs: any) => ({
      ...prevConfigs,
      [plotId]: {
        ...prevConfigs[plotId],
        ...config
      }
    }));
  };

  const updatePlotConfigWithHeatmapProtection = (plotId: any, config: PlotConfig) => {
    if (config.plotType !== 'heatmap' &&  tableItems.selectedItems['Z-Axis Data'].length > 0) { 
      if (tableItems.selectedItems['Y-Axis Data'].length > 0 && tableItems.selectedItems['Y-Axis Data'][0].method === 'categorical') {
        tableItems.setItems(({
          ...tableItems.selectedItems,
          'Y-Axis Data': tableItems.selectedItems['Z-Axis Data'],
          'Z-Axis Data': tableItems.selectedItems['Y-Axis Data'],
        }));
      } 
      else if (tableItems.selectedItems['X-Axis Data'].length > 0 && tableItems.selectedItems['X-Axis Data'][0].method === 'categorical') {
        tableItems.setItems(({
          ...tableItems.selectedItems,
          'X-Axis Data': tableItems.selectedItems['Z-Axis Data'],
          'Z-Axis Data': tableItems.selectedItems['X-Axis Data'],
        }));
      }
    }
    else if (config.plotType === 'heatmap') {
      // console.log("items here", tableItems.selectedItems)
      if (tableItems.selectedItems['Y-Axis Data'].length > 0 && tableItems.selectedItems['Y-Axis Data'][0].method === 'data') {
        const categoricalAxis = 
          (tableItems.selectedItems['Z-Axis Data'][0].method === 'categorical') ?  
            tableItems.selectedItems['Z-Axis Data'] 
            : [{
              name: "Sample Title",
              versionType: null,
              method: "index",
              values: ["Sample Title"],
              pipelines: [],
            }]
        tableItems.setItems(({
          ...tableItems.selectedItems,
          'Y-Axis Data': categoricalAxis,
          'Z-Axis Data': tableItems.selectedItems['Y-Axis Data'],
        }));
      } 
      else if (tableItems.selectedItems['X-Axis Data'].length > 0 && tableItems.selectedItems['X-Axis Data'][0].method === 'data') {
        const categoricalAxis = 
          (tableItems.selectedItems['Z-Axis Data'][0].method === 'categorical') ?  
            tableItems.selectedItems['Z-Axis Data'] 
            : [{
              name: "Sample Title",
              versionType: null,
              method: "index",
              values: ["Sample Title"],
              pipelines: [],
            }]
        tableItems.setItems(({
          ...tableItems.selectedItems,
          'X-Axis Data': categoricalAxis,
          'Z-Axis Data': tableItems.selectedItems['X-Axis Data'],
        }));
      }
    }
    updatePlotConfig(plotId, config)
  };

  const handleSavePartitionSampleGroups = (partition: number | undefined, sampleGroups: Record<string, number[]>) => {

  }

  const handleRefresh = () => {
    setRefresh(prevKey => prevKey + 1);
  };

  

  const handlePlotError = (error: any, plotIds:number[]) => {
    setPlotError(error)
    setLoadingPlots(prev => {
      return prev.filter(p => !plotIds.includes(p))
    });
  }

  // const sampleGroupColorDict = useDeepCompareMemo(() => {
    
  //   // create color dict for sample group dict 
  //   let sampleGroupColorDict: any = {}
  //   let idx = 0
  //   for (let key in sampleGroupDict) {
  //     sampleGroupColorDict[key] = defaultColorMap[idx % 12]
  //     idx += 1
  //   }

  //   return sampleGroupColorDict

  // }, [sampleGroupDict])

  const {sampleColorDict, activeSampleGroupDict} = useDeepCompareMemo(() => {

    let sampleColorDict: any = {}
    let activeSampleGroupDict: any = {}

    activeSampleGroupDict['ORDER'] = structuredClone(sampleGroup)
    if (Object.keys(sampleGroupToSampleDict).length !== 0 && Object.keys(tumorDict).length > 0) {
      for (let sg of sampleGroup) {
        if (sg.id !== undefined && (sg.id in sampleGroupToSampleDict)) {

          let color = sampleGroupColorDict[sg.id]
          for (let sample of sampleGroupToSampleDict[sg.id]) {
            if (tumorDict[sample]) {
              sampleColorDict[sample] = color
              let shortenedSample = tumorDict[sample].split('-')
              activeSampleGroupDict[shortenedSample[shortenedSample.length - 1]] = sg.value
            }
          }
        }
      }
    }

    return {sampleColorDict, activeSampleGroupDict}

  }, [sampleGroup, tumorDict])
  

  // const {plotLayers, plotLayout: plotLayout, 
  //       plotLoading: plotLoading, 
  //       categoryAxes, dataAxes, plotAlerts} = useGeneratePlot(plotConfigs.plot1, filteredPlotData, 
  //                                                             filtersState, axisFiltersState, 
  //                                                             advancedSortingState, tableItems.selectedItems, loadedData.categoricalColumns, 
  //                                                             fetchedObjectsChanged, tileState, numTileColumns, groupedSampleMode, activeSampleGroupDict,
  //                                                             () => {setFetchedObjectsChanged(new Set()); setPlotError('')},
  //                                                             handlePlotError);
  
  
  

  const plotConfigurations = useMemo(() => {
    // console.log("Blah", editedMultiPlotData, axisFiltersState, fetchedObjectsChanged)
    

    const outConfigs =  Object.entries(memoizedEditedMultiPlotData)
      .filter(([_plotId, _]) => 
        selectedPlotIds.includes(parseInt(_plotId))
      )
      .filter(([_, plotData]) => 
          plotData.selectedItems !== null && 
          plotData.selectedItems !== undefined &&
          plotData.configuration !== null &&
          plotData.configuration !== undefined
      )
      .map(([_plotId, plotData]) => {
        const plotId: number = parseInt(_plotId)
        // console.log("Id", plotId)
        const pathwayIdOrder = structuredClone(plotData.configuration?.pathways?.map((sd: SearchableDropdownOption) => sd.id)) ?? []
        const pathwayKeySet = new Set(pathwayIdOrder);
        const outGroups: Groups = Object.fromEntries(Object.entries(structuredClone(pathwayDict)).filter(([k, v]) => pathwayKeySet.has(parseInt(k))))

        const finalOverride = plotData.configuration?.colorOverride || {}
        // console.log("Final Override", structuredClone(finalOverride), structuredClone(colorOverrideMap));
        for (let key in colorOverrideMap){
          if (!(key in finalOverride)){
            finalOverride[key] = colorOverrideMap[key]
          }
        }
        return ({
          plotConfig: plotData.configuration,
          filtersState,
          axisFiltersState: plotData.axisFiltersState,
          // vieweableAxisFilters,
          advancedSortingState,
          selectedItems: plotData.selectedItems,
          categoricalData: loadedData.categoricalColumns,
          fetchedObjectsChanged,
          tileState: plotData.tileState,
          tileSharedY,
          tileSharedX,
          setNumTileColumns,
          groupedSampleMode,
          activeSampleGroupDict,
          plotId: plotId,
          groups: outGroups,
          groupOrder: pathwayIdOrder,
      })
    });
      return outConfigs;

  }, [memoizedEditedMultiPlotData, advancedSortingState,
      loadedData.categoricalColumns, fetchedObjectsChanged, tileSharedY, tileSharedX,
      numTileColumns, groupedSampleMode, activeSampleGroupDict, pathwayDict]);

  const [loadingStates, setLoadingStates] = useState<boolean[]>(plotConfigurations.map(() => false));
  const [loadingMessages, setLoadingMessages] = useState<string[]>(plotConfigurations.map(() => 'Generating Plot...'));

  const PlotGridPlots = useGeneratePlots(
      plotConfigurations,
      filteredPlotData,
      (finishedPlotIds: number[]) => { // onPlotSucess function: removed the sucessfully created plots from the loading state
          setFetchedObjectsChanged(new Set());
          setPlotError('');
          // console.log("Finishing this")
          setLoadingPlots(prev => {
            return prev.filter(p => !finishedPlotIds.includes(p))
          });
      },
      handlePlotError,
      setLoadingStates,
      setLoadingMessages,
      
  );

  const [plots, setPlots] = useState<any>(PlotGridPlots);

  useEffect(() => {
    setPlots(PlotGridPlots);
    // console.log("plots: ", plots);
  },[PlotGridPlots])

  

  const {
    plotLayers,
    plotLayout,
    categoryAxes,
    dataAxes,
    plotAlerts
  } = useMemo(() => plots.filter((p: any) => p.plotId === selectedMultiPlot)[0] || {plotLayers: [], plotLayout: [], plotLoading:true}, [selectedMultiPlot, plots]); // Use empty object as fallback if plots[0] is undefined

  const plotLoading = useMemo(() => loadingStates[plotConfigurations.findIndex(pc => pc.plotId === selectedMultiPlot)] || false, [loadingStates, selectedMultiPlot, plotConfigurations]);
  const plotLoadingMessage = useMemo(() => loadingMessages[plotConfigurations.findIndex(pc => pc.plotId === selectedMultiPlot)] || 'Generating Plot...', [loadingMessages, selectedMultiPlot, plotConfigurations]);

  const convertToFormattedPlotData = (data: DataRow[]): DataRow[] => {
    return data
  }

  const sortArrayByProperty = (array: any, property: any, isAscending: boolean) => {
    return array.sort((a: string, b: string) => {
      const valueA = a[property].toUpperCase(); // to handle case-insensitive sorting
      const valueB = b[property].toUpperCase();
  
      if (valueA < valueB) {
        return isAscending ? -1 : 1;
      }
      if (valueA > valueB) {
        return isAscending ? 1 : -1;
      }
      return 0;
    });
  };

  const handleTile = (tileKey: string) => {

    // if tileState is empty, means that user is turning on tiling, in which case we should turn on tileSharedX only if there is more than one row
    if (Object.keys(tileState).length === 0 && plotLayoutFinal.grid?.rows && plotLayoutFinal.grid.rows > 1) {
      setTileSharedX(true);
    }
     
    setTileState((prevState) => ({
      ...prevState,
      [tileKey]:
        prevState[tileKey] ? false : true
    }));
  }

  const disableAllTile = () => {
    const keys = Object.keys(tileState)
    let newState: TileState = {}
    for (let key of keys){
      // console.log(key)
      newState[key] = false
    }
    setTileState(newState)
  }

  useEffect(()=> {
    if (selectedPlotIds.length > 1){
      disableAllTile()
    }
  }, [isCompareMode, selectedPlotIds])

  const handleAddTileColumn = () => {
    // setNumTileColumns((prevNumColumns) => prevNumColumns + 1);
    setPlotConfigs((prevConfigs: any) => ({
      plot1: {
        ...prevConfigs['plot1'],
        tileMode: 'fixedColumn',
        tileFixedColumnCount: numTileColumns + 1,
      }
    }));
  }

  const handleRemoveTileColumn = () => {
    // If adding more than one row, turn on shared X to prevent overlap of x-ticks on subplots spanning multiple rows
    if (plotLayoutFinal.grid?.rows && plotLayoutFinal.grid.rows > 1) {
      setTileSharedX(true);
    }
    // setNumTileColumns((prevNumColumns) => prevNumColumns - 1);
    setPlotConfigs((prevConfigs: any) => ({
      plot1: {
        ...prevConfigs['plot1'],
        tileMode: 'fixedColumn',
        tileFixedColumnCount: numTileColumns - 1,
      }
    }));
  }

  const handleBasicSort = (sortKey: any, sortOption: string) => {

    // console.log("sortKey: ", sortKey);
    // console.log("filtersState: ", filtersState);
    // console.log("axisFiltersState: ", axisFiltersState);

    setBasicSortingState((prevState) => ({
      ...prevState,
      [sortKey]:
        prevState[sortKey] === 'ascending' ? 'descending' :
          prevState[sortKey] === 'descending' ? undefined : 'ascending',
    }));
  
    const isAscending = sortOption === 'ascending';
  
    if (sortKey === 'Sample Title') {
      setSample((prevSamples) => sortArrayByProperty(prevSamples, 'label', isAscending));
    } else if (sortKey === 'Treatment_and_Dose') {
      setTreatment((prevTreatment) => sortArrayByProperty(prevTreatment, 'label', isAscending));
    } else if (sortKey === 'Timepoint') {
      setTimepoint((prevTimepoint) => sortArrayByProperty(prevTimepoint, 'label', isAscending));
    } else if (sortKey in axisFiltersState) {
      setAxisFiltersState((prevState) => {
        const newState = { ...prevState };
        newState[sortKey].value = sortArrayByProperty(newState[sortKey].value, 'label', isAscending);
        return newState;
      });
    } 

    // else if (sortKey in filtersState) {
    //   setFiltersState((prevState) => {
    //     const newState = { ...prevState };
    //     newState[sortKey] = sortArrayByProperty(newState[sortKey], 'label', isAscending);
    //     return newState;
    //   });
    // } 

  }

  const handleSetAxisFiltersState = useCallback((key: any, newValue: any, selected:number) => {
    
    // console.log("setting axisFiltersState: ", newValue, key);
    const timeID = setTimeout(()=>setAxisFiltersState(prevState => {
      const existingValue = prevState[key] || [];
      // console.log("Setting here ", prevState)
      if (!deepEqual(existingValue, newValue)) {
        return {
          ...prevState,
          [key]: {
            ...prevState[key],
            value: newValue
          }
        };
      }
      return prevState; // Return the existing state if values are the same to avoid unnecessary re-render
    }),0);
    return () => {
      // console.log("clearing timeout")
      clearTimeout(timeID);
    }
  }, [setAxisFiltersState]);

  const handleAddGenesNano = () => {
    setAddGeneModelOpenNano(true);
  }

  const handleAddGenesSeq = () => {
    setAddGeneModelOpenSeq(true);
  }

  

  // useEffect(() => {
  //   for (let k of Object.keys(axisFiltersState)) {
  //     const key = parseInt(k) as number;
  //     const filter = axisFiltersState[key]
  //     if (filter.versionType === 'RNA-Seq 1.0' || filter.versionType === 'RNA 1.0'){

  //       handleAddPathways(pathways.map(p => p.id as number), filter.versionType); 
  //     }
        
  //   }
  // }, [pathways])

  const checkIsPathwayMode = useCallback(() => {
    if (tableItems.selectedItems === undefined) {
      return false;
    }
    const currentSelectedItems = tableItems.selectedItems;
    let isPathwayMode = false;
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] && currentSelectedItems[axis][0].method === 'data' &&currentSelectedItems[axis][0].pathwayMode) {
        isPathwayMode = true;
      }
    }
    return isPathwayMode
  }, [tableItems.selectedItems]);

  const checkPathwayModeVersionType = useCallback(() => {
    if (tableItems.selectedItems === undefined) {
      return null;
    }
    const currentSelectedItems = tableItems.selectedItems;
    let pathwayVT = null;
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] && currentSelectedItems[axis][0].method === 'data' 
        && currentSelectedItems[axis][0].versionType !== null && 
        (versionTypeDict[currentSelectedItems[axis][0].versionType ?? 0] == 'RNA 1.0' || versionTypeDict[currentSelectedItems[axis][0].versionType ?? 0] == 'RNA-Seq 1.0')) {
        pathwayVT = currentSelectedItems[axis][0].versionType;
      }
    }
    return pathwayVT
  }, [tableItems.selectedItems]);

  const checkRelativeMode = useCallback(() => {
    if (tableItems.selectedItems === undefined) {
      return false;
    }
    const currentSelectedItems = tableItems.selectedItems;
    let isRelativeMode = false;
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] && currentSelectedItems[axis][0].method === 'data' && currentSelectedItems[axis][0].processingMode === 'relative') {
        isRelativeMode = true;
      }
    }
    return isRelativeMode
  }, [tableItems.selectedItems]);

  const getDataColumn = useCallback(() => {
    if (tableItems.selectedItems === undefined) {
      return null;
    }
    const currentSelectedItems = tableItems.selectedItems;
    let dataColumn = null;
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] && currentSelectedItems[axis][0].method === 'data' && currentSelectedItems[axis][0].values.length > 0) {
        dataColumn = currentSelectedItems[axis][0].values[0];
      }
    }
    return dataColumn
  }, [tableItems.selectedItems]);


  // update pathways names when the dict changes
  useEffect(() => {
    setPathways(prev => {
      return prev.map(sd => {
        if (sd.id !== undefined) {
          return {'id':sd.id, 'label':pathwayDict[sd.id].name, 'value':pathwayDict[sd.id].name}
        }
        else {
          return sd
        }
      })
    })
  }, [pathwayDict])

  const RNA_CELL_TYPES = ['B-cells', 'CD8 T cells', 'Cytotoxic cells', 'Exhausted CD8', 'Macrophages', 'Mast cells', 'Neutrophils', 'NK CD56dim cells',
    'NK cells', 'T-cells']
  const handleAddPathwayGroups = (versionType: string) => {
    setPathwayGroupAddModalVersionType(versionType);
  } 


  const isPathwayMode = useMemo(() => {
    if (tableItems.selectedItems === undefined) {
      return false;
    }
    const currentSelectedItems = tableItems.selectedItems;
    let isPathwayMode = false;
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] && currentSelectedItems[axis][0].method === 'data' &&currentSelectedItems[axis][0].pathwayMode) {
        isPathwayMode = true;
      }
    }
    return isPathwayMode
  }, [tableItems.selectedItems]);

  const pathwayOptions = useMemo(() => {
    return Object.keys(pathwayDict).map(key => ({ id: Number(key), label: pathwayDict[Number(key)].name, value: pathwayDict[Number(key)].name }));
  }, [pathwayDict]);

  useEffect(() => {
    if (pathways && !deepEqual(pathways, plotConfigs.plot1.pathways) && isPathwayMode && loadingPlots.length === 0) { 
      updatePlotConfig('plot1', {'pathways':structuredClone(pathways)});
    }
  }, [pathways, isPathwayMode, loadingPlots])


  const handleAddPathways = useCallback((pathways: number[], versionType: string) => {
    if (isPathwayMode) {
      return;
    }
    
    setAxisFiltersState(prevState => {
      const matchingFilter: number | undefined = getFilterKeys(prevState).find((key:number) => prevState[key].column === 'Gene' && prevState[key].versionType === versionType);
      if (matchingFilter !== undefined && prevState[matchingFilter].type === 'categorical' && customFilterOptions[`${versionType}|Gene`] !== undefined) {
        const currentOptions = new Set(customFilterOptions[`${versionType}|Gene`].map((option: SearchableDropdownOption) => option.value));
        // const currentValue = prevState[matchingFilter].value.map((value: SearchableDropdownOption) => value.value);
        let newValues: string[] = []
        Object.entries(pathwayDict).filter(([id, entry]) => pathways.includes(Number(id))).map(([k, v]) => v).forEach(({name, values}) => {
          newValues = [...newValues, ...values];
        })
        // const filteredValue: string[] = Array.from(new Set([...currentValue, ...newValues])).filter((value: string) => currentOptions.has(value)); // use this line if we want to append new pathways
        const filteredValue: string[] = Array.from(new Set([...newValues])).filter((value: string) => currentOptions.has(value)); // use this line if we want to overwrite the filter with the pathway
        const combinedValues: SearchableDropdownOption[] = filteredValue.map((value: string) => { return {'label': value, 'value': value}});
          
        return ({
          ...prevState,
          [matchingFilter]: {
            ...prevState[matchingFilter],
            value: combinedValues
          } as CategoricalFilter
        });
      }
      else {
        return prevState; // Return the existing state if no matching filter is found
      }
    });
  }, [customFilterOptions, pathwayDict, isPathwayMode]);

  const setAndAddPathways = useCallback((pathways: React.SetStateAction<SearchableDropdownOption[]>, versionType: string) => {
    if (Array.isArray(pathways)){
      handleAddPathways(pathways.map(p => p.id as number), versionType);
    }
    console.log("Setting and adding pathways: ", pathways);
    setPathways(pathways);

  }, [handleAddPathways]);

  const togglePathwayMode = useCallback(() => {
    if (tableItems.selectedItems === undefined) {
      return;
    }
    const currentSelectedItems = structuredClone(tableItems.selectedItems)
    // const isPathwayMode = checkIsPathwayMode();
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] !== undefined && currentSelectedItems[axis][0].method === 'data') {
        currentSelectedItems[axis][0].pathwayMode = !currentSelectedItems[axis][0].pathwayMode;
        if (currentSelectedItems[axis][0].pathwayScore === undefined) {
          currentSelectedItems[axis][0].pathwayScore = 'mean';
        }
      } 
    }

    tableItems.setItems(currentSelectedItems)
  }, [tableItems.selectedItems])


  const getAxisDataFromPlot = useCallback((vt: string, column: string): string[] => {
    // Gets the data column used in the plot for the given version type and column name
    // finds if the data column is used in any of the axes, then returns the tick values of that axis from plotLayersFinal
    if (tableItems.selectedItems === undefined) {
      return [];
    }
    const currentSelectedItems = tableItems.selectedItems;
    const finalValues: Set<string> = new Set<string>();
    for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
      if (currentSelectedItems[axis] && currentSelectedItems[axis][0] && currentSelectedItems[axis][0].method === 'categorical' 
        && currentSelectedItems[axis][0].versionType !== null 
        && currentSelectedItems[axis][0].name === vt
        && currentSelectedItems[axis][0].values.length > 0
        && currentSelectedItems[axis][0].values[0] === column) {
        // found the axis, now get the tick values from plotLayersFinal
        for (let layer of plotLayersFinal) {
          const tickList = layer[axis.toLowerCase()[0]]
          if (tickList !== undefined) {
            if (axis === 'Y-Axis Data') {
              tickList.reverse();
            }
            tickList.forEach((tick: any) => {
              finalValues.add(tick);
            });
          }
        }
      }
    }
    return Array.from(finalValues);
  }, [tableItems.selectedItems, plotLayersFinal]);

  const getPathwayDropdown =  (pathways: SearchableDropdownOption[], key: string, isPathwayMode: boolean, onTile: (() => void)| undefined, tile: boolean | undefined, onAdd: () => void, customButton: any, pathwayOptions: SearchableDropdownOption[], togglePathwayMode: () => void, versionType: string, setAndAddPathways: (pathways: React.SetStateAction<SearchableDropdownOption[]>, versionType: string) => void) =>{
    return (<SortableCard
        key={key+'pathway'}
        showTile={isPathwayMode}
        onTile={onTile}
        tile={tile}
        title={'Pathways'} 
        showAddButton={(onAdd !== undefined) ? true : false}
        onAdd={onAdd}
        customButton={
          <>
            <Tooltip title={(isPathwayMode) ? 'Disable Pathway Mode': 'Enable Pathway Mode'}>
              <IconButton onClick={() => togglePathwayMode()}>
                <CallMerge sx={{fontSize: 'medium', color: (isPathwayMode) ? 'blue':'grey'}}/>
              </IconButton>
            </Tooltip>
            {customButton}
          </>
        }
          
      >
        <SearchableDropdown
            label="Pathways"
            value={pathways}
            setValue={(newValue) => setAndAddPathways(newValue, versionType)}
            multiple={true}
            options={pathwayOptions}
        />
      </SortableCard>)
  };

  

  const [tempNanoGeneFilter, setTempNanoGeneFilter] = useState<SearchableDropdownOption[] | undefined>(undefined);
  const [tempNanoGeneFilterKey, setTempNanoGeneFilterKey] = useState<number | undefined>(undefined);
  const handleTempNanoGeneFilter = (newValue: any, key: number, selected:number) => {
    setTempNanoGeneFilterKey(key);
    setTempNanoGeneFilter(newValue);
  }

  const [tempSeqGeneFilter, setTempSeqGeneFilter] = useState<SearchableDropdownOption[] | undefined>([]);
  const [tempSeqGeneFilterKey, setTempSeqGeneFilterKey] = useState<number | undefined>(undefined);
  const handleTempSeqGeneFilter = (newValue: any, key: number, selected:number) => {
    setTempSeqGeneFilterKey(key);
    setTempSeqGeneFilter(newValue);
  }

  const handleApplyNanoGeneFilter = useCallback(() => {
    handleSetAxisFiltersState(tempNanoGeneFilterKey, tempNanoGeneFilter, selectedPlotTab);
    setTimeout(() => setTempNanoGeneFilter(undefined), 0);
  }, [tempNanoGeneFilter, tempNanoGeneFilterKey, selectedPlotTab, handleSetAxisFiltersState]);

  const handleApplySeqGeneFilter = useCallback(() => {
    handleSetAxisFiltersState(tempSeqGeneFilterKey, tempSeqGeneFilter, selectedPlotTab);
    setTimeout(() => setTempSeqGeneFilter(undefined), 0);
  }, [tempSeqGeneFilter, tempSeqGeneFilterKey, selectedPlotTab, handleSetAxisFiltersState]);


  const createPathwayFromGeneList = useCallback((geneList: string[], versionType: string) => {
    // create new pathway from gene list
    // open add pathway modal with the new pathway
    handleCreateGeneGroupFromGeneList(geneList).then((newPathwayID: number | null) => {
      if (newPathwayID !== null) {
        if (versionType === 'RNA 1.0'){
          setDefaultOpenPathwayIdNano(newPathwayID);
          setPathwayEditModalOpenNano(true); 
        }
        else {
          setDefaultOpenPathwayIdSeq(newPathwayID);
          setPathwayEditModalOpenSeq(true);
        }
      }
    });
  }, [handleAddPathways]);

  const getAxisFilters = useMemo(() => {
    let ts = [];
    // const isPathwayMode = checkIsPathwayMode();
    const pathwayVTid = checkPathwayModeVersionType();
    const relativeMode = checkRelativeMode();
    const pathwayVT = (pathwayVTid !== null) ? versionTypeDict[pathwayVTid] : undefined;
    let isPathwayFilterAdded = false;

    for (let k of Object.keys(axisFiltersState)) {
      const key = parseInt(k) as number
      const filter = axisFiltersState[key]
      const type = filter.type;

      if (filter.viewable){

        let combinedFilterLabel = `${filter.column}`;
        let nonIndexedLabel = `${filter.versionType}|${filter.column}`;

        if (filter.type === 'numeric') {
          combinedFilterLabel = `${filter.column} (${filter.level})`;
          if (!relativeMode && filter.level === 'relative') {
            continue;
          }
        }

        let onAdd = undefined;
        let customButton = undefined; 
        let customButtonPathway = undefined;
        let apply = undefined;
        let onApply = undefined;
        let applyButtonDisabled = false;
        let handle = (newValue: any) => {
          handleSetAxisFiltersState(key, newValue, selectedPlotTab);
        }
        let filterValue = undefined;

        if (filter.column === 'Gene' && filter.type === 'categorical' ) {
          filterValue = filter.value;

          onAdd = () => {
            handleAddPathwayGroups(filter.versionType);
          }
          if (filter.versionType === 'RNA 1.0' && filter.column === 'Gene'){
            customButton = <Tooltip title='Create Pathway From Filter'>
                            <IconButton aria-label="" onClick={() => createPathwayFromGeneList(filter.value.map((v: SearchableDropdownOption) => v.value), 'RNA-Seq 1.0')}> 
                                  <CreateNewFolder sx={{fontSize: 'medium', color: 'grey'}}/>
                            </IconButton>
                          </Tooltip> 
            customButtonPathway = <IconButton aria-label="" onClick={() => setPathwayEditModalOpenNano(true)}> 
                                <SettingsIcon sx={{fontSize: 'small', color: 'grey'}}/>
                          </IconButton>
            if (selectedSamples.length > 30) {
              onApply = () => handleApplyNanoGeneFilter();
              apply = true;
              handle = (newValue: any) => {
                handleTempNanoGeneFilter(newValue, key, selectedPlotTab);
              }
              if (tempNanoGeneFilter !== undefined && deepEqual(tempNanoGeneFilter, filterValue)) {        
                applyButtonDisabled = true;
              }
              else if (tempNanoGeneFilter !== undefined) {
                filterValue = tempNanoGeneFilter;
                applyButtonDisabled = false;
              }
              else {
                applyButtonDisabled = true;
              }

            }
          }
          else if (filter.versionType === 'RNA-Seq 1.0' && filter.column === 'Gene'){
            customButton = <Tooltip title='Create Pathway From Filter'>
                            <IconButton aria-label="" onClick={() => createPathwayFromGeneList(filter.value.map((v: SearchableDropdownOption) => v.value), 'RNA-Seq 1.0')}> 
                                  <CreateNewFolder sx={{fontSize: 'medium', color: 'grey'}}/>
                            </IconButton>
                          </Tooltip> 
            customButtonPathway = <IconButton aria-label="" onClick={() => setPathwayEditModalOpenSeq(true)}> 
                                <SettingsIcon sx={{fontSize: 'small', color: 'grey'}}/>
                          </IconButton>
            if (selectedSamples.length > 30) {
              onApply = () => handleApplySeqGeneFilter();
              apply = true;
              handle = (newValue: any) => {
                handleTempSeqGeneFilter(newValue, key, selectedPlotTab);
              }
              if (tempSeqGeneFilter !== undefined && deepEqual(tempSeqGeneFilter, filterValue)) {         
                applyButtonDisabled = true;
              }
              else if (tempSeqGeneFilter !== undefined) {
                filterValue = tempSeqGeneFilter;
                applyButtonDisabled = false;
              }
              else {
                applyButtonDisabled = true;
              }
              
            }
          }
          // const pathwayOptions = Object.keys(pathwayDict).map(key => ({ id: Number(key), label: pathwayDict[Number(key)].name, value: pathwayDict[Number(key)].name }));
          let tile = (isPathwayMode) ? tileState[nonIndexedLabel]: undefined;
          let onTile = (isPathwayMode) ? () => handleTile(nonIndexedLabel) : undefined
          ts.push(
            getPathwayDropdown(pathways, k, isPathwayMode, onTile, tile, onAdd, customButtonPathway, pathwayOptions, togglePathwayMode, filter.versionType, setAndAddPathways)
          )
          isPathwayFilterAdded = true;
          if (isPathwayMode) {
            continue
          }
        }
        if ( filter.type === 'categorical' ){
          if (filterValue === undefined) {
            filterValue = filter.value;
          }
          customButton = (<><Tooltip title={'Remove Filtered '+ filter.column + 's from List'}>
                            <IconButton aria-label="" onClick={() => handle(getAxisDataFromPlot(filter.versionType, filter.column).map(v => ({'value':v, 'label':v})))}> 
                                  <PlaylistRemove sx={{fontSize: 'large', color: 'grey'}}/>
                            </IconButton>
                          </Tooltip>
                          {customButton}</>
                        );
        }


        // console.log("filterValue: ", filterValue);
        
        let optionsKey = `${filter.versionType}|${filter.column}`;
    
        ts.push(
          <SortableCard
            key={key}
            showTile={type === 'categorical' && showTile}
            onTile={() => handleTile(nonIndexedLabel)}
            tile={tileState[nonIndexedLabel]}
            title={combinedFilterLabel} 
            // showAddButton={(onAdd !== undefined) ? true : false}
            // onAdd={onAdd}
            applyDisabled={applyButtonDisabled}
            showApplyButton={apply}
            onApply={onApply}
            showSort={type === 'categorical'}
            onSort={type === 'categorical' ? (sortOption) => handleBasicSort(key, sortOption) : undefined}
            customButton={customButton}
            // onSort={(sortOption) => handleBasicSort('axisFiltersState', sortOption)}
          > 

            {filter.type === 'categorical' && filterValue && <SearchableDropdown 
              label={combinedFilterLabel}
              value={filterValue}
              setValue={handle}
              options={customFilterOptions[optionsKey] !== undefined ? customFilterOptions[optionsKey] : []}
            />}
            {type === 'numeric' && <NumericFilterInput
              filterValue={filter.value}
              setFilterValue={handle}

            ></NumericFilterInput>}
          </SortableCard>
        );
      }
    }
    if (!isPathwayFilterAdded && pathwayVT !== undefined) {
      // const pathwayOptions = Object.keys(pathwayDict).map(key => ({ id: Number(key), label: pathwayDict[Number(key)].name, value: pathwayDict[Number(key)].name }));
      let nonIndexedLabel = `${pathwayVT}|Gene`;
      let tile = (isPathwayMode) ? tileState[nonIndexedLabel]: undefined;
      let onTile = (isPathwayMode) ? () => handleTile(nonIndexedLabel) : undefined
      let customButton = undefined;
      if (pathwayVT === 'RNA 1.0'){
        customButton = <IconButton aria-label="" onClick={() => setPathwayEditModalOpenNano(true)}> 
                            <SettingsIcon sx={{fontSize: 'small', color: 'grey'}}/>
                      </IconButton>
      }
      else if (pathwayVT === 'RNA-Seq 1.0'){
        customButton = <IconButton aria-label="" onClick={() => setPathwayEditModalOpenNano(true)}> 
                            <SettingsIcon sx={{fontSize: 'small', color: 'grey'}}/>
                      </IconButton>
      }
      ts.splice(0, 0, 
        getPathwayDropdown(pathways, 'pathways', isPathwayMode, onTile, tile, () => {handleAddPathwayGroups(pathwayVT);}, customButton, pathwayOptions, togglePathwayMode, pathwayVT, setAndAddPathways)
      )
      const handleAddGeneFilter = () => {
        const geneFilter: CategoricalFilter = {
          viewable: true,
          column: 'Gene',
          versionType: pathwayVT,
          type: 'categorical',
          filterBehavior: 'include',
          value: [] as SearchableDropdownOption[],
        }
        setAxisFiltersState(prevState => {
          const highestKey = Object.keys(prevState).length > 0 ? Math.max(...Object.keys(prevState).map(Number)) : 0;
          const newKey = highestKey + 1;
          return {
            ...prevState,
            [newKey]: geneFilter
          }
        });
      }
      const addGeneFilterButton = 
        <Card elevation={0}>
          <CardHeader
            title={
              <Box flexDirection={'row'} display={'flex'} alignItems={'center'}>
                <Typography component={"h2"} variant="subtitle1" sx={{ color: '#131313' }}>Add Gene Filter</Typography>
                <Tooltip title="Add Gene Filter">
                  <IconButton aria-label="" onClick={() => handleAddGeneFilter()}> 
                    <AddCircleOutline sx={{fontSize: 'large', color: 'grey'}}/>
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
        </Card>
      ts.push(addGeneFilterButton)
    }
    return ts;  
  }, [axisFiltersState, customFilterOptions, tileState, showTile, selectedPlotTab, pathways, pathwayDict, isPathwayMode, pathwayOptions, tempNanoGeneFilter, tempSeqGeneFilter, selectedSamples, plotLayersFinal, togglePathwayMode, setAndAddPathways, getAxisDataFromPlot]);
  

  const plotData = useDeepCompareMemo(() => convertToFormattedPlotData(loadedData.data), [loadedData.data]);

  const handleStudyChange = async (newStudy: SearchableDropdownOption[], setSampleDropdown: boolean, includeQCFail: boolean, setSampleGroupOptions?: boolean) => {
   
    if (newStudy.length) {
    
      try {
        const studyQueryString = newStudy.map((s) => s.id).join(',');
        if (studyQueryString) {
          const response = await axios.get(`/tumors/api/v1/get_samples_for_study/?studies=${studyQueryString}&include_qc_fail=${includeQCFail}`);
          const associatedSamplesFromAPI = response.data.samples;
        
          const newSamples: SearchableDropdownOption[] = []
          const updatedSamples = associatedSamplesFromAPI.map((sample: string) => ({id: Number(sample), label: tumorDict[Number(sample)], value: tumorDict[Number(sample)]}));
          
          tempSample.forEach((s) => {
            if (s.id && !(associatedSamplesFromAPI.includes(s.id))) {
              newSamples.push(s);
            }
          });

          if (setSampleDropdown) {
            setTempSample([...newSamples, ...updatedSamples]);
            setStudy(newStudy);
          }

          if (setSampleGroupOptions) {
            setPartitionSampleOptions(updatedSamples);
          }
        }
      } catch (error) {
        handleAPIError(error);
      }
    }
    
  };

  const handleAPIError = (error: any) => {
    const serverError = error as AxiosError;
    if (serverError && serverError.response) {
      if (serverError.response.status === 403) {
        window.location.href = '/accounts/login/?next=/fe/samples';  // Redirecting to login if there's a 403 error
      }
      console.error("Server responded with an error:", serverError.response.data);
    } else {
      console.error("API error:", error);
    }
  };

  const handleRequestDeletePlotGroup = (id: number) => {
    setDeleteTargetId(id);
    setConfirmDeletePlotGroupOpen(true);
  };

  const handleRequestDeletePlot = (id: number) => {
    setDeleteTargetId(id);
    setConfirmDeletePlotOpen(true);
  };

  const handleCloseConfirmPlotGroupDeleteDialog = () => {
    setConfirmDeletePlotGroupOpen(false);
    setDeleteTargetId(null);
  };

  const handleCloseConfirmPlotDeleteDialog = () => {
    setConfirmDeletePlotOpen(false);
    setDeleteTargetId(null);
  };

  const handleRenamePlot = (id: number) => {
    setRenameTargetId(id);
    setRenameDialogOpen(true);
  }

  const handleCustomPlotSaveSuccess = (id: number, switchPlot?: boolean | null) => {
    refetchPlotGroups().then(() => {
        // Update selectedPlotIds and selectedMultiPlot only after refetch completes
        if (!selectedPlotIds.includes(id)){
          setSelectedPlotIds([id]);
        }
        setSelectedMultiPlot(id)
        if (!switchPlot) {
          setSelectedPlotTab(0);
        } else {
          setSelectedPlotTab(1);
        }
    });
  };

  const handleSavePlotSuccess = (plotId: number) => {
    refetchPlotGroups().then(
      ()=> {
        multiPlotData[plotId].refetchPlotDetails();
        setSaveSuccess(true);
      }
    );
    
  }


  // const handleSaveAsPlot = useCallback((name: string, plotGroupId: number, isPublic: boolean)=> {
  //   if (tableItems.selectedItems) {
  //     saveCustomPlot(true, tableItems.selectedItems, axisFiltersState, name, plotGroupId, isPublic, plotConfigs.plot1, dataManipulation, (id: number) => handleCustomPlotSaveSuccess(id, false));
  //     setCurrentPlotName(name);
  //   } 
  // }, [savePlotModalOpen]);


  const handleSaveAsPlot = (name: string, plotGroupId: number, isPublic: boolean)=> {
    if (tableItems.selectedItems) {
      saveCustomPlot(true, tableItems.selectedItems, axisFiltersState, name, plotGroupId, isPublic, plotConfigs.plot1, dataManipulation, pathways, tileState, (id: number) => handleCustomPlotSaveSuccess(id, false));
      setCurrentPlotName(name);
    }
  };

  const handleAddNewPlot = (name: string, plotGroupId: number, isPublic: boolean) => {
    if (tableItems.selectedItems) {
      saveCustomPlot(true, {'X-Axis Data': [], 'Y-Axis Data': []}, {}, name, plotGroupId, isPublic, initialPlotConfig, dataManipulation, [], {}, (id: number) => handleCustomPlotSaveSuccess(id, true));
      setVersionType([]); 
      setSelectedMultiPlot(-1);
      // console.log("Setting version type to empty")
      setAddPlot(false);
      setCurrentPlotName(name);
    }
  };

  // const handleAddNewPlot = useCallback((name: string, plotGroupId: number, isPublic: boolean) => {
  //   if (tableItems.selectedItems) {
  //     saveCustomPlot(true, {'X-Axis Data': [], 'Y-Axis Data': []}, {}, name, plotGroupId, isPublic, initialPlotConfig, dataManipulation, (id: number) => handleCustomPlotSaveSuccess(id, true));
  //     setVersionType([]); 
  //     setAddPlot(false);
  //     setCurrentPlotName(name);
  //   }
  // }, [savePlotModalOpen]);


  const handleSavePlot = () => {
    if (tableItems.selectedItems) {
      if (selectedPlotIds.length > 0 && selectedMultiPlot in flatPlotDict) {
        const plotSettings = flatPlotDict[selectedMultiPlot]
        console.log("Tile State", tileState)
        saveCustomPlot(false, tableItems.selectedItems, axisFiltersState, currentPlotName, plotSettings.plot_group, plotSettings.public, plotConfigs.plot1, dataManipulation, pathways, tileState, () => handleSavePlotSuccess(selectedMultiPlot));
      }
    }
  };
  

  const handleConfirmDeletePlotGroup = () => {
    if (deleteTargetId !== null) {
      deletePlotGroup(deleteTargetId)
        .then(() => {
          // Deletion was successful
          setConfirmDeletePlotGroupOpen(false);
          // Optionally refresh plot groups or show success message
        })
        .catch((error) => {
          // Handle error, e.g., show an error message
          console.error("Deletion failed", error);
          // Optionally show error message to the user
        });
    }
  };

  const handleConfirmDeletePlot = () => {
    if (deleteTargetId !== null) {
      const deleteFunction = multiPlotData[deleteTargetId]?.deleteCustomPlot !== undefined ? 
        multiPlotData[deleteTargetId]?.deleteCustomPlot : 
          async () => {
            try {
              const csrfToken = getCookie("csrftoken");
              await axios.delete(`/tumors/api/v1/plottemplates/${deleteTargetId}`, {
                headers: {
                  "X-CSRFToken": csrfToken,
                },
              });
              // console.log("Successfully deleted");
              // TODO: need to refresh custom plot component and flush local browser storage cache with stale plot ID
            } catch (err) {
              const serverError = err as AxiosError;
              if (serverError && serverError.response) {
                if (serverError.response.status === 403) {
                  window.location.href = '/accounts/login/?next=' + encodeURIComponent(window.location.pathname + window.location.search);
                }
              } else {
                console.error("Failed to delete plot group", err);
              }
            }
          };

      if (deleteFunction) {
        deleteFunction()
          .then(() => {
            if (selectedPlotIds.includes(deleteTargetId)){
              setSelectedPlotIds(selectedPlotIds.filter((i) => deleteTargetId !== i))
            }
            setConfirmDeletePlotOpen(false);
            refetchPlotGroups();
          })
          .catch((error: any) => { 
            console.error("Deletion failed", error);
          });
      } else {
        console.error("Delete function not found for the specified plot");
      }
    } else {
      console.error("No plot selected for deletion");
    }
  };

  const handleSubmitRenamePlot = (name: string) => {
    if (renameTargetId !== null) {

      const renameFunction = async () => {
        try {
          const csrfToken = getCookie("csrftoken");
          await axios.put(`/tumors/api/v1/plottemplates/${renameTargetId}`, {'name':name},{
            headers: {
              "X-CSRFToken": csrfToken,
            },
          });
        } catch (err) {
          const serverError = err as AxiosError;
          if (serverError && serverError.response) {
            if (serverError.response.status === 403) {
              window.location.href = '/accounts/login/?next=' + encodeURIComponent(window.location.pathname + window.location.search);
            }
          } else {
            console.error("Failed to Rename Plot ", err);
          }
        }
      };
      renameFunction().then(() => {
        refetchPlotGroups();
        setRenameTargetId(null);
        setRenameDialogOpen(false);
      })
    }
  }

  const handleAddPlotGroup = () => {
    setAddPlotGroupModalOpen(true); // Open the modal
  };

  const handleCloseAddPlotGroupModal = () => {
    setAddPlotGroupModalOpen(false); // Close the modal
  };

  const handleAddSamples = () => {
    setAddSamplesModalOpen(true);
  };

  const handleApplyStudyFilter = (studies: SearchableDropdownOption[], includeQCFail: boolean) => {
    setStudy(studies);
    handleStudyChange(studies, true, includeQCFail);
    setAddSamplesModalOpen(false);
  }

  /////// USE EFFECTS ///////

  useEffect(() => {
    const sampleIds = sample.map(s => s.id).filter(id => id !== undefined).join(',');
    const currentSearchParams = new URLSearchParams(window.location.search);
    if (sampleIds) currentSearchParams.set('samples', sampleIds);
    const readableUrl = `${window.location.pathname}?${currentSearchParams.toString()}`.replace(/%2C/g, ',');

    // Replace the current state in the history without navigating
    const newUrl = `${window.location.pathname}?${currentSearchParams.toString()}`;
    window.history.replaceState({ path: newUrl }, '', newUrl);
  }, [sample])

  useEffect(() => {
    if (loadingPlots.length > 0) {
      let toAdd: SearchableDropdownOption[] = []
      for (let plotID of selectedPlotIds){
        if (!(plotID in flatPlotDict)){
          continue
        }
        if (flatPlotDict[plotID]){
          let versionTypes = flatPlotDict[plotID].version_types;
          for (let key of versionTypes) {
            let versionTypeDropdown = {id: Number(key), label: versionTypeDict[key], value: versionTypeDict[key]}
            if (!toAdd.map(obj => obj.id).includes(Number(key))){
              toAdd.push(versionTypeDropdown)
            }
          }
        }
      }
      if (toAdd.length > 0) {
        // console.log("setting version type results.tsx 1222: ", toAdd)
        // setVersionType(prev => prev.concat(toAdd.filter(sd => !prev.map(psd => psd.id).includes(sd.id))))
        setVersionType(toAdd);
      }
    }
  }, [sample, study, flatPlotDict, loadingPlots, selectedPlotIds])

  
  useEffect(() => {
    let newPartitionOptions: SearchableDropdownOption[] = Object.keys(partitionDict).map(key => ({id: Number(key), label: partitionDict[Number(key)], value: partitionDict[Number(key)]}))
    if (newPartitionOptions.length > 0)
      setPartitionOptions(newPartitionOptions)
  }, [partitionDict])

  useEffect(() => {
    if (partition.length > 0 && partition[0].id !== undefined && Object.keys(sampleGroupDict).length > 0) {
      let sampleGroups = partitionToSampleGroupDict[partition[0].id] || [];
      let newSampleGroupOptions = sampleGroups.map((key: number) => ({id: key, label: sampleGroupDict[key], value: sampleGroupDict[key]}))
      setSampleGroupOptions(newSampleGroupOptions)
      setSampleGroup(newSampleGroupOptions)
    }
  }, [partition, sampleGroupDict])


  useEffect(() => {
    if (partition.length > 0) {

      let partitionName = partition[0].value
      let study = partitionName.split(' - ')[0]

      const studyDictReverse: {[key: string]: number} = {};
      Object.keys(studyDict).forEach(key => {
        const numKey = Number(key);
        if (!isNaN(numKey)) {
          let key: string = studyDict[numKey];
          studyDictReverse[key] = numKey;
        }
      });

      let studyId = studyDictReverse[study]
      let studyOption = {id: studyId, label: study, value: study}
      handleStudyChange([studyOption], false, false, true)
    }

  }, [partition, studyDict])

  useEffect(() => {
      let newTreatmentOptions = Object.keys(treatmentDict).map(key => ({id: Number(key), label: treatmentDict[Number(key)].name, value: treatmentDict[Number(key)].name}))
      if (newTreatmentOptions.length > 0 && !deepEqual(newTreatmentOptions, treatmentOptions)){
        setTreatmentOptions(newTreatmentOptions) 
      }
  }, [treatmentDict])

  useEffect(() => {
      let newTimepointOptions = Object.keys(timepointDict).map(key => ({id: Number(key), label: timepointDict[Number(key)], value: timepointDict[Number(key)]}))
      if (newTimepointOptions.length > 0)
        setTimepointOptions(newTimepointOptions)
  }, [timepointDict])

  useEffect(() => {
    let newVersionTypeOptions = Object.keys(versionTypeDict).map(key => ({id: Number(key), label: versionTypeDict[Number(key)], value: versionTypeDict[Number(key)]}))
    if (newVersionTypeOptions.length > 0)
      setVersionTypeOptions(newVersionTypeOptions)
  }, [treatmentDict])

  useEffect(() => {
    for (let key in loadedData.categoricalColumns) {
      let optionsList = loadedData.categoricalColumns[key] || [];
      if (!(key in customFilterOptions)) {
        setCustomFilterOptions(prevState => ({
          ...prevState,
          [key]: optionsList.map((key: string) => ({label: key, value: key}))
        }));
      }
    }
  }, [loadedData.categoricalColumns])

  // Sets initial layout of the page
  useEffect(() => {
    let boardId = 1;
    fetchUserOptions();
  }, []);

  useEffect(() => {
    setTempSample(sample)

    // console.log("setTempSample: ", sample);
    
    // Reset treatment options to avoid MUI AutoSelect error while new treatment options are populating
    // setTreatmentOptions([]);
    // Reset timepoint options to avoid MUI AutoSelect error while new timepoint options are populating
    // setTimepointOptions([]);

  }, [sample])
  
  useEffect(() => {
    let timeout: any;
    if (saveSuccess) {
      // Reset the save success state after 3000ms (3 seconds)
      timeout = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }

    // Cleanup the timeout when the component is unmounted or the saveSuccess state changes
    return () => clearTimeout(timeout);
  }, [saveSuccess]);


  useEffect(() => {
    let errorMessage = ''

    if (isDataError) {
      errorMessage = dataErrorMessage 
    }
    else if (plotError) {
      errorMessage = plotError
    }
    else if (tooManyDataPointsError) {
      errorMessage = 'Too Many Data Points'
    }
    else if (loadingMessage !== initialLoadingMessage && selectedPlotIds.length !== 0 && selectedVersionTypes.length === 0 && loadingPlots.length === 0) {
      errorMessage = 'Please Select an Analysis Type'
    }
    // console.log("Setting plot error: ", errorMessage)
    setErrorMessage(errorMessage)

  }, [isDataError, plotError, dfMessage,   tooManyDataPointsError, selectedVersionTypes]);

  useEffect(() => {
    let tooManyError = false

    let xCount = 0
    let yCount = 0 
    for (let layer of plotLayers ? plotLayers : []) {
      if (plotConfigs.plot1.plotType !== 'heatmap') {

        if (layer['x']) {
          xCount += layer['x'].length
        }
        if (layer['y']) {
          yCount += layer['y'].length
        }
      }
    }
   
    if (xCount > 1000000 || yCount > 1000000) {
      tooManyError = true
    }

    setTooManyDataPointsError(tooManyError)
    if (!tooManyError) {
      setPlotLayersFinal(plotLayers)
      setPlotLayoutFinal(plotLayout)
    }

  }, [plotLayers, plotLayout])

 

  const updateFilters = () => {
    // const axisFilterValues   = getAxisFiltersFromSelectedItems(tableItems.selectedItems)

    // setAxisFiltersState(prevState => {       

    //   const newState = { ...prevState };
  
    //   // Remove filters that are not present in the current state
    //   for (let filter in prevState) {
    //     if (!axisFilterValues.hasOwnProperty(filter)) {
    //       delete newState[filter];
    //     }
    //   }
    //   return {
    //     ...newState,
    //     ...axisFilterValues
    //   }})
    
    // setViewableAxisFiltersState(vieweableAxisFilters)
    
  }

  // useEffect(() => {
  //   updateFilters()
  // }, [tableItems.selectedItems])
  const loadingDebounceTimer = useRef<NodeJS.Timeout>()

  const [plotRendering, setPlotRendering] = useState(false)

  useEffect(() => {
    if (selectedPlotIds.length === 0){
      console.log("setIsLoading(false)");
      setIsLoading(false);
      setErrorMessage('Please Select a Plot');
    }
    // if (selectedVersionTypes.length === 0){
    //   setIsLoading(false);
    //   setErrorMessage('Please Select an Analysis Type')
    //   return;
    // }
    // If any loading state is true, immediately set isLoading to true
    // console.log("---------")
    // console.log("plotLoading: ", plotLoading);
    // console.log("dfProgress: ", dfProgress);
    // console.log("taskCompleted: ", !taskCompleted); 
    // console.log("dataProcessLoading: ", dataProcessLoading); 
    // console.log("dataFrameIsLoading: ", dataFrameIsLoading); 
    // console.log("loadingPlots: ", loadingPlots); 
    // console.log("plotRendering: ", plotRendering);
    // if (plotLoading || (dfProgress && dfProgress != 100)  || dataProcessLoading || !taskCompleted || dataFrameIsLoading) {
    if (plotLoading || (dfProgress && dfProgress != 100)  || dataProcessLoading || dataFrameIsLoading || !taskCompleted || loadingPlots.length > 0 || plotRendering) {
      if (loadingDebounceTimer.current !== undefined){
        // console.log("cancelling", loadingDebounceTimer.current)
        clearTimeout(loadingDebounceTimer.current)
      }
      
      if (dfProgress && dfProgress != 100) {
        setLoadingMessage(dfMessage);
      }
      else if (plotLoading) {
        setLoadingMessage(plotLoadingMessage);
      }
      else if (dataProcessLoading) {
        setLoadingMessage('Processing Data');
      }
      else if (dataFrameIsLoading) {
        setLoadingMessage('Loading Data');
      }
      else if (!taskCompleted) {
        setLoadingMessage('Fetching Plot Template');
      }
      else if (loadingPlots.length > 0) {
        setLoadingMessage('Loading Plots');
      }
      else if (plotRendering) {
        setLoadingMessage('Rendering Plot...');
      }

      // console.log("setIsLoading(true)");
      setIsLoading(true);
    } else {
      // When all loading states are false, debounce the setting of isLoading to false
      const timerId = setTimeout(() => {
        // console.log("setIsLoading(false)", timerId);
        setIsLoading(false);
      }, 500); // Adjust the delay as necessary, was 300 12/17/24 changed to 500
      // console.log("Starting clear ", timerId)
      loadingDebounceTimer.current = timerId
      // return () => clearTimeout(timerId);
    }
  }, [plotLoading, dfProgress, dataProcessLoading, taskCompleted, dataFrameIsLoading, selectedVersionTypes, loadingPlots, plotRendering]);
  
  const convertToGroupedOptions = (dataColumns: Record<string, string[]> | undefined, dataPipelineDict: any): OptionGroup[] => {  
    if (!dataColumns || Object.keys(dataColumns).length === 0) {
        return [];
    }

    dataColumns['Sample Group'] = [""]
  
    const versionTypeDictReverse: {[key: string]: number} = {};
    Object.keys(versionTypeDict).forEach(key => {
      const numKey = Number(key);
      if (!isNaN(numKey)) {
        let key: string = versionTypeDict[numKey];
        versionTypeDictReverse[key] = numKey;
      }
    });

    return Object.keys(dataColumns).map((parent: string) => {
      const childArray = dataColumns[parent];

      // Convert each string in childArray to an OptionValue object
      const options: OptionValue[] = childArray.map(child => {
        return {
          label: child,
          value: child,
          id: parent in versionTypeDictReverse ? Number(versionTypeDictReverse[parent]) : undefined
        };
      });

      const calculatedOptions: OptionValue[] = []
      Object.keys(dataPipelineDict).forEach((pipeline: any) => {
        dataPipelineDict[pipeline]['result_columns'].forEach((column: any) => {
          const match : OptionValue | undefined = options.find((ele) => ele.value === column.name && column.version_type === Number(versionTypeDictReverse[parent]))
          if (match){
            match.pipeline = pipeline
          }
          else { 
            if (column.version_type === versionTypeDictReverse[parent]){
              calculatedOptions.push({
                label: column.name,
                value: column.name,
                pipeline: pipeline,
                id: Number(column.version_type)
              })
            }
          }
        })
      })
      options.push(...calculatedOptions)
      return {
        label: parent,
        id: parent in versionTypeDictReverse ? Number(versionTypeDictReverse[parent]) : undefined,
        options: options
      };
    });
  };
  const groupedOptions = useDeepCompareMemo(() => convertToGroupedOptions(loadedData.columns, dataPipelineDict), [loadedData.columns, dataPipelineDict]);


  const layout = {

    title: '',
    xaxis: {
      title: '',
      showgrid: true,
      zeroline: true
    },
    yaxis: {
      title: '',
      showline: true
    },
  };

  // Iterate over keys of PlotTypeDict and make a dictionary of options, capitalize the first letter
  const plotTypeOptions = Object.keys(plotTypeDict).map((key) => {
    let label = plotTypeDict[key];
    return { label: (label.charAt(0).toUpperCase() + label.slice(1)), id: Number(key), value: label };
  });

  const zOptions = ['Count', 'Log2_Fold_Change'].map(option => ({'label': option, 'value': option, 'id': undefined}))

  const getControlGroupsData = () => {
    const baseControlGroups: ControlGroup[] = [
      {
        title: 'Plot Settings',
        controls: [
          { 
            label: 'Plot Type', 
            type: 'dropdown', 
            id: 'plotType',
            textFieldLabel: 'Plot Name',
            textFieldValue: selectedPlotIds ? flatPlotDict[selectedMultiPlot]?.name : '',
            options: plotTypeOptions, 
            defaultValue: 'scatter' 
          }
        ],
      },
      {
        title: 'X-Axis',
        controls: [
          { label: 'X-Axis Data', id: 'xAxes', type: 'axis controls', options: groupedOptions },
        ],
      },
      {
        title: 'Y-Axis',
        controls: [
          { label: 'Y-Axis Data', id: 'yAxes', type: 'axis controls', options: groupedOptions },
        ],
      },
    ];

    const zAxisControlGroup: ControlGroup = {
      title: 'Z-Axis',
      controls: [
        { label: 'Z-Axis Data', id: 'zAxes', type: 'axis controls', options: groupedOptions, disableSubheaders: true },
      ],
    };

    // Conditionally add Z-Axis controls for 'heatmap' plot type
    if (plotConfigs.plot1.plotType === 'heatmap') {
      return [...baseControlGroups, zAxisControlGroup];
    }

    return baseControlGroups;
  };

  const controlGroupsData = useDeepCompareMemo(getControlGroupsData, [plotConfigs.plot1.plotType, groupedOptions, plotTypeDict, selectedMultiPlot, selectedPlotIds]);

  const fetchAnalysesFromFilters = async (sampleIds: number[], timepointIds: number[], treatmentIds: number[]) => {
    if (sampleIds.length === 0 || timepointIds.length === 0 || treatmentIds.length === 0) { 
      return []
    }
    const response = await axios.get(`/tumors/api/v1/analyses/?sample=${sampleIds.join(',')}&timepoints=${timepointIds.join(',')}&treatments=${treatmentIds.join(',')}`);
    const analyses: Analysis[] = response.data
    return analyses
  }

  useEffect(() => {
    // Need to make this more efficient
    if (plotData) {
      setIsDataProcessLoading(true)

      let plottedIndexColumns: string[] = []

      for (let tile of Object.keys(tileState)) {
        let splitTile = tile.split('|')
        let tileKey = splitTile[0]
        plottedIndexColumns.push(tileKey)
      }

      for (let key in tableItems.selectedItems) {
        if (tableItems.selectedItems[key]) {
          for (let item of tableItems.selectedItems[key]) {
            if (['Treatment_and_Dose', 'Timepoint', 'Sample Title', 'Sample Group', 'channel_id'].includes(item.name)) {
              if (!plottedIndexColumns.includes(item.name))
                plottedIndexColumns.push(item.name)
            }
          }
        }
      }

      // Set the sort order for the remaining index columns to enforce the order of subgroups
      // This is a workaround to ensure that the order of the subgroups is maintained
      // while still allowing the user to sort by other columns
      for (let key of ['Treatment_and_Dose', 'Timepoint', 'Sample Title', 'Sample Group', 'channel_id']) {
        // Check if the key is not already in plottedIndexColumns
        if (!plottedIndexColumns.includes(key)) {
          plottedIndexColumns.push(key);
        }
      }

      setIndexSortOrder(plottedIndexColumns)

      // Flatten the values for filtering and sorting
      const flattenedTreatment = [...treatment.map(t => t.value), 'N/A'];
      const flattenedTimepoint = [...timepoint.map(t => t.value), 'N/A'];
      
      const filterNullSamples = sample.filter(s => s.value !== undefined)
      const flattenedSample = filterNullSamples.map(s => s.value.split('-')[s.value.split('-').length - 1]);
  
      // Get index function for sorting
      const getOrderIndex = (item: any, indexMap: Record<string, number>, key: string) => {
        if (key in item && Object.keys(indexMap).length) {
          const index = indexMap[item[key]];
          return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        }
        return Number.MAX_SAFE_INTEGER;
      };
  
      const sampleIndexMap = getIndexMapOfStrArray(flattenedSample);
      const timepointIndexMap = getIndexMapOfStrArray(flattenedTimepoint);
      const treatmentIndexMap = getIndexMapOfStrArray(flattenedTreatment);

      // Filter and sort the data
      let sortedAndFilteredData = plotData.filter((item: any) => {
        if ('Treatment_and_Dose' in item && flattenedTreatment.length && !flattenedTreatment.includes(item['Treatment_and_Dose'])) {
          return false;  
        }
        if ('Timepoint' in item && flattenedTimepoint.length && !flattenedTimepoint.includes(item['Timepoint'])) {
          return false;  
        }
        if ('Sample Title' in item && flattenedSample.length && !flattenedSample.includes(item['Sample Title'])) {
          return false;  
        }
        return true;
      }).sort((a: any, b: any) => {
        for (const column of plottedIndexColumns) {
          let indexMap: Record<string, number> = {};
          if (column === 'Sample Title') {
            indexMap = sampleIndexMap;
          } else if (column === 'Treatment_and_Dose') {
            indexMap = treatmentIndexMap;
          } else if (column === 'Timepoint') {
            indexMap = timepointIndexMap;
          }
  
          const indexA = getOrderIndex(a, indexMap, column);
          const indexB = getOrderIndex(b, indexMap, column);
  
          if (indexA !== indexB) {
            return indexA - indexB;
          }
        }
        return 0;
      });

      
      const sampleIds = flattenedSample.map((s) => parseInt(Object.keys(tumorDict).find(key => tumorDict[Number(key)].split('-').pop() === s) ?? "-1")).filter((s) => s !== undefined && s !== -1);
      const timepointIds = flattenedTimepoint.map((t) => parseInt(Object.keys(timepointDict).find(key => timepointDict[Number(key)] === t) ?? "-1")).filter((t) => t !== undefined && t !== -1);
      const treatmentIds = flattenedTreatment.map((t) => parseInt(Object.keys(treatmentDict).find(key => treatmentDict[Number(key)].name === t) ?? "-1")).filter((t) => t !== undefined && t !== -1);

      fetchAnalysesFromFilters(sampleIds, timepointIds, treatmentIds).then((analyses) => setFilteredAnalyses(analyses))

      setFilteredPlotData(sortedAndFilteredData);
    setTimeout(()=> setIsDataProcessLoading(false), 0);
    }
  }, [plotData, treatment, timepoint, sample, sampleGroup, basicSortingState, tileState]);

  

  useEffect(() => {
    if (memoizedEditedMultiPlotData) {
      if (Object.keys(memoizedEditedMultiPlotData).length >= 0 && fetchedVersionTypes && !isCustomPlotLoading && Object.keys(versionTypeDict).length > 0) {
        const versionTypeList: number[] = loadingPlots.length === 0 ? Array.from(new Set(selectedVersionTypes.concat(getVersionTypesFromMultiPlot(memoizedEditedMultiPlotData)))) : getVersionTypesFromMultiPlot(memoizedEditedMultiPlotData); 
        
        if (loadingPlots.length > 0 && Object.keys(versionTypeDict).length > 0){
          // console.log("setting version types results.tsx 1888: ", versionTypeList.map((versionType: number) => ({id: versionType, label: versionTypeDict[versionType], value: versionTypeDict[versionType]})) )
          setVersionType(versionTypeList.map((versionType: number) => ({id: versionType, label: versionTypeDict[versionType], value: versionTypeDict[versionType]})))
        }
        else if (versionTypeList.length !== 0 && Object.keys(versionTypeDict).length > 0){ // If version type list is zero, we want to avoid setting the version types so that the user has a chance to fill out the plot
          setVersionType(prevVersionTypes => {
            // console.log("Previous version types: ", prevVersionTypes)
            const prevVTList : number[] = []; //prevVersionTypes.map((vt) => vt.id).filter((id) => id !== undefined) as number[]
            const newVTList = Array.from(new Set([...versionTypeList, ...prevVTList]))
            const newVersionTypes = newVTList.map((versionType: number) => ({id: versionType, label: versionTypeDict[versionType], value: versionTypeDict[versionType]}))
            // console.log("Setting version types results.tsx 1896: ", newVersionTypes)
            return newVersionTypes
          });
        }

        let configChanged = fetchedConfiguration && !deepEqual(plotConfigs.plot1, fetchedConfiguration)
        let selectedItemsChanged = fetchedSelectedItems && !deepEqual(tableItems.selectedItems, fetchedSelectedItems)
        let axisFilterChanged = fetchedAxisFilter && !deepEqual(axisFiltersState, fetchedAxisFilter)
        let pathwaysChanged = fetchedConfiguration.pathways && !deepEqual(pathways, fetchedConfiguration.pathways)
        let tileStateChanged = fetchedTileState && !deepEqual(tileState, fetchedTileState)

        let objectsChanged: Set<string> = new Set()
        if (pathwaysChanged && fetchedConfiguration.pathways) {
          // console.log("Setting pathways plot loading: ", pathways)
          objectsChanged.add('pathways')
          setPathways(structuredClone(fetchedConfiguration.pathways))
        }
        if (configChanged) {
          setPlotConfigs({plot1: fetchedConfiguration as PlotConfig});
          objectsChanged.add('plotSettings')
        }
        if (selectedItemsChanged) {
            objectsChanged.add('selectedItems')
        }
        if (axisFilterChanged) {
          setAxisFiltersState(fetchedAxisFilter);
          // console.log('setting', fetchedAxisFilter)
          objectsChanged.add('axisFilters')
        }
        if (tileStateChanged) {
          setTileState(fetchedTileState);
          objectsChanged.add('tileState')
        }
        // console.log("setting fetchedConfiguration");
        // console.log(fetchedConfiguration);
        
        setFetchedObjectsChanged(objectsChanged)
       
      } 

      if (selectedMultiPlot === 0){
        // console.log("Setting multi plot here")
        setSelectedMultiPlot(selectedPlotIds[0])
      }
    }

  }, [taskCompleted, fetchedConfiguration, fetchedSelectedItems, fetchedVersionTypes, fetchedAxisFilter, fetchedTileState, isCustomPlotLoading, versionTypeDict, pathwayDict])


  useEffect(() => {
    if (Object.keys(multiPlotData).length < 2 || true) {
      const prevFetchedSelectedItems = prevFetchedSelectedItemsRef.current;
      // console.log("We are here", taskCompleted, isCustomPlotLoading, fetchedSelectedItems, prevFetchedSelectedItems);
      
      
      if (!isCustomPlotLoading && fetchedSelectedItems && prevFetchedSelectedItems === null){
        // console.log("setting items here", fetchedSelectedItems)
        tableItems.setItems(fetchedSelectedItems);        
        prevFetchedSelectedItemsRef.current = fetchedSelectedItems;

      }
      else if (!isCustomPlotLoading && fetchedSelectedItems && (!deepEqual(fetchedSelectedItems, prevFetchedSelectedItems) && !deepEqual(fetchedSelectedItems, tableItems.selectedItems))) {
        // console.log("setting items here2", fetchedSelectedItems)
        tableItems.setItems(fetchedSelectedItems);
        prevFetchedSelectedItemsRef.current = fetchedSelectedItems;
      }
      // else {
      //   tableItems.setItems({})
      //   prevFetchedSelectedItemsRef.current = null;
      // }
      // prevFetchedSelectedItemsRef.current = fetchedSelectedItems;

    }
  }, [taskCompleted, isCustomPlotLoading, fetchedSelectedItems])

  useEffect(() => {

    // Parse the query string
    const queryParams = new URLSearchParams(location.search);
    const sampleIdsQuery = queryParams.get('samples')?.split(','); // Assuming 'samples' is the query parameter
    const studyIdsQuery = queryParams.get('study')?.split(','); // Assuming 'samples' is the query parameter
    
    if (Object.keys(tumorDict).length === 0) {
      return;
    }
    
    let newTumorNames: any = []
    if (sampleIdsQuery) {
      for (let sampleId of sampleIdsQuery) {
        let numberId = Number(sampleId)
        if (tumorDict[numberId]) {
          newTumorNames.push({id: numberId, label: tumorDict[numberId], value: tumorDict[numberId]})
        }
      }
    }

    let newStudyNames: any = []
    if (studyIdsQuery) {
      for (let studyId of studyIdsQuery) {
        let numberId = Number(studyId)
        if (studyDict[numberId]) {
          newStudyNames.push({id: numberId, label: studyDict[numberId], value: studyDict[numberId]})
        }
      }
    }

    if (newStudyNames) {
      handleStudyChange(newStudyNames, true, false)
      setStudy(newStudyNames)
    }

    if (newTumorNames) {
      setSample(newTumorNames);
    }
    sampleLoadedFromUrl.current = true;
    

    if (sample.length === 0) {
      setSample(newTumorNames || []);
    }

  }, [location, tumorDict, studyDict]);

  useEffect(() => {
    const previousSampleGroup = previousSampleGroupRef.current;
    const previousSampleGroupToSampleDict = previousSampleGroupToSampleDictRef.current;

    if (Object.keys(sampleGroupToSampleDict).length === 0 || Object.keys(tumorDict).length === 0) {
      return;
    }

    
    if (sampleGroup.length > previousSampleGroup.length || 
        !deepEqual(sampleGroupToSampleDict, previousSampleGroupToSampleDict)) {
      
      // Get the IDs of the current tempSample items
      let tempSampleIds = tempSample.map((sample) => sample.id);
      
      // Use a Set to store the samples to add
      let samplesToAdd = new Set<SearchableDropdownOption>();
      
      for (let sg of sampleGroup) {
        if (sg.id !== undefined && sg.id in sampleGroupToSampleDict) {
          let samples = sampleGroupToSampleDict[sg.id];
          samples.forEach((sample: number) => {
            let sampleToAdd: SearchableDropdownOption = { id: sample, label: tumorDict[sample], value: tumorDict[sample] };
            if (!tempSampleIds.includes(sample)) {
              samplesToAdd.add(sampleToAdd);
            }
          });
        }
      }
      
      // Update the tempSample state
      setTempSample([...tempSample, ...Array.from(samplesToAdd)]);
    } else if (sampleGroup.length < previousSampleGroup.length) {
      // Find removed sample groups
      const removedGroups = previousSampleGroup.filter(prevSG =>
        !sampleGroup.some(currSG => currSG.id === prevSG.id)
      );
      
      // Find samples that belong to removed groups
      let samplesToRemoveIds = new Set<number>();
      for (let sg of removedGroups) {
        if (sg.id !== undefined && sg.id in previousSampleGroupToSampleDict) {
          let samples = previousSampleGroupToSampleDict[sg.id];
          samples.forEach((sample: number) => {
            samplesToRemoveIds.add(sample);
          });
        }
      }
      
      // Update the tempSample state by removing samples that belong to removed groups
      let newSample = tempSample.filter(sample => sample.id && !samplesToRemoveIds.has(sample.id));
      setTempSample(newSample);
    }

    // Update the refs to the current states after the effect runs
    previousSampleGroupRef.current = sampleGroup;
    previousSampleGroupToSampleDictRef.current = sampleGroupToSampleDict;
  }, [sampleGroup, sampleGroupToSampleDict, tumorDict, tempSample]);

  useEffect(() => {
    // default to all timepoints selected
    if (timepoint.length === 0){
      setTimepoint(Object.keys(timepointDict).map(key => ({id: Number(key), label: timepointDict[Number(key)], value: timepointDict[Number(key)]})))
    }
  }, [timepointDict])

  useEffect(() => {
    // default to all treatments selected
    if (treatment.length === 0){ 
      setTreatment(Object.keys(treatmentDict).map(key => ({id: Number(key), label: treatmentDict[Number(key)].name, value: treatmentDict[Number(key)].name})))
    }
  }, [treatmentDict])

  useEffect(() => {
    if (versionType.length > 0){
      if (dataManipulation !== null && dataPipelineDict[dataManipulation] !== undefined){ 
        let versionTypeIds: number[] = []
        versionType.map((value: any) => {
          versionTypeIds.push(value.id)
        })
        let isValidDataManip = dataPipelineDict[dataManipulation]['version_types'].every((val:number) => versionTypeIds.includes(val))
        if (!isValidDataManip){
          setDataManipulation(null)
        }
      }
    }
    else {
      setDataManipulation(null)
    }
  }, [selectedVersionTypes])

  /////////////////////

  // Set default plot group to ID=1 when saving or saving as plots
  const defaultPlotGroup = plotGroups.find(group => group.id === 1) || null;

  const handlePlotSelection = (plotIds: number[]) => {
    
    // console.log("Handle plot selection", plotIds)
    
    setSelectedPlotIds(plotIds);  
    // Causes error when switching from P0854 spheroid metrics plot to P1363 sample (with only BF results avail): 
    // Results.tsx:1793 Warning: Cannot update a component (Results) while rendering a different component (ForwardRef(DataGrid)). 
    
    
    // if (plotIds.length > 0) {
    //   setCurrentPlotName(flatPlotDict[selectedMultiPlot]?.name);
    // } else {
    //   setCurrentPlotName('');
    // }
  };

  const handleResetPlot = (plotId: number) => {
    setSelectedPlotIds([plotId]);
  };

  const handleSaveSampleGroup = (newSampleGroups: any, sampleGroupColors: any) => {
    let partitionId: number | undefined = partition && partition.length > 0 ? partition[0].id : undefined
    if (partitionId !== undefined) {
      updatePartition({id: partitionId, sampleGroups: newSampleGroups, sampleGroupColors: sampleGroupColors})
    }  
  }

  const handleSaveGeneGroup = (id: number, payload: Groups) => {
    if (payload[id].readonly) {
      return;
    } 
    const csrfToken = getCookie("csrftoken");
    axios.put(`/tumors/api/v1/pathways/${id}`, payload[id], {headers: {"X-CSRFToken": csrfToken}}).then(
      response => {
        fetchPathways();
      }
    )
  }

  const handleCreateGeneGroupFromGeneList = async (geneList: string[]) => {
    const csrfToken = getCookie("csrftoken");
    const newID = await axios.post('/tumors/api/v1/pathways', {name: 'New Gene Group', genes:geneList}, {headers: {"X-CSRFToken": csrfToken}}).then(
      async (response:any) => {
        await fetchPathways();
        return response.data.id;
      }
    )
    return newID;
  }

  const handleCreateEmptyGeneGroup = async () => {
    const csrfToken = getCookie("csrftoken");
    const newID = await axios.post('/tumors/api/v1/pathways', {name: 'New Gene Group'}, {headers: {"X-CSRFToken": csrfToken}}).then(
      async (response:any) => {
        await fetchPathways();
        return response.data.id;
      }
    )
    return newID;
  }

  const handleDeleteGeneGroup = (id: number) => {
    const csrfToken = getCookie("csrftoken");
    axios.delete(`/tumors/api/v1/pathways/${id}`, {headers: {"X-CSRFToken": csrfToken}}).then(
      response => {
        fetchPathways();
      }
    )
  }

  const handleContentChange = (id: number, newContent: string | number[] | {width: number | undefined, height: number | undefined}) => {
    const newContentToSave = { ...contentToSave, [id]: { content: newContent } };
    setContentToSave(newContentToSave);
  };

  const handleDropdownApply = useCallback(() => {
    setSample([...tempSample]); 
    setApplySampleDisabled(true);
  }, [tempSample, sampleGroupColorDict]);

  // Hook to update the sample list if and only if the order of the samples change without needing to click apply
  // This allows users to utilize the drag and drop ordering much easier in the sample dropdown
  // Can be removed if it makes things too slow or we can put a limit on this so that the user can reorder larger lists without the plot updating
  useEffect(() => { 
    let sameSamples = false;
    let sameOrder = false;
    const sampleReorderCap = 10; // if the temp sample list length exceeds this, the apply button will show up for reorders 
    if (isEqual(sample, tempSample)){
      sameOrder = true
    }
    if (sample.length > 0 && tempSample.length < sampleReorderCap && sample.length === tempSample.length && !sameOrder){
      const sampleSet = new Set(sample)
      const tempSampleSet = new Set(tempSample)
      sameSamples = sampleSet.size === tempSampleSet.size && [...tempSampleSet].every(item => sampleSet.has(item));
    }
    if (sameSamples && !sameOrder && tempSample.length < sampleReorderCap){
      handleDropdownApply()
    }
  }, [tempSample])

  const handleDelete = (key: number, type: string) => {
    const csrfToken = getCookie("csrftoken");
  
    axios.delete(`/tumors/api/v1/delete_content/${key}`, {
      headers: {"X-CSRFToken": csrfToken}
    })
    .then(response => {
      // remove the card from unstagedData layout and content_ids
      const currentPageData = unstagedData[currentPage];
      const newLayout = currentPageData.layout.filter(layout => layout.i !== key.toString());
      const newContentIds = currentPageData.content_ids.filter(id => id !== key);
  
      const newUnstagedData = { 
        ...unstagedData, 
        [currentPage]: {
          ...currentPageData, 
          layout: newLayout, 
          content_ids: newContentIds
        }
      };
  
      setUnstagedData(newUnstagedData);
    })
    .catch(error => {
      // handle error
      console.log(error);
    });
  };

  // Function to handle opening the drawer
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  // Function to handle closing the drawer
  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleSplitHorizontal = () => {
    if (!isCompareMode) {
      setIsCompareMode(true);
    }
    setIsHorizontalLayout(true);
  };

  const handleSplitVertical = () => {
    if (!isCompareMode) {
      setIsCompareMode(true);
    }
    setIsHorizontalLayout(false);
  };

  const handleSinglePlot = () => {
    // console.log(selectedPlotIds);
    const lastSelected = selectedPlotIds[selectedPlotIds.length - 1];
    setIsCompareMode(false);
    if (selectedPlotIds.length > 1) {
        setSelectedPlotIds([lastSelected]);
    }
  };


  const sortingGroupedOptions = useDeepCompareMemo(() => {

    let sortingOptions: any = []
    for (let option of groupedOptions) {
      let newOptions: any[] = option.options
      
      if (option.label === 'channel_id') {
        continue
      }

      if (option.label === 'Sample Title') {
        newOptions = sample.map(s => {return {label: s.label, value: s.value}});
        let newGroupOption = {'label': option.label, 'id': undefined, 'options':  newOptions} 
        sortingOptions.push(newGroupOption)
      }

      else if (option.label === 'Treatment_and_Dose') {
        newOptions = treatment.map(s => {return {label: s.label, value: s.value}});
        let newGroupOption = {'label': option.label, 'id': undefined, 'options':  newOptions} 
        sortingOptions.push(newGroupOption)
      }

      else if (option.label === 'Timepoint') {
        newOptions = timepoint.map(s => {return {label: s.label, value: s.value}});
        let newGroupOption = {'label': option.label, 'id': undefined, 'options':  newOptions} 
        sortingOptions.push(newGroupOption)
      }

      else {
        newOptions = []
        for (let axis in categoryAxes) {
          for (let category in categoryAxes[axis]) {
            if (category === option.label) {
              newOptions = [...newOptions, ...Object.keys(categoryAxes[axis][category]).map((item: any) => {return {label: item, value: item}})];
            }
          }
        }
          
        for (let axis in dataAxes) {
          for (let nonCategory in dataAxes[axis]) {
              newOptions = [...newOptions, ...dataAxes[axis][nonCategory]['values'].map((item: any) => {return {label: item, value: item}})];
          }
        }
       
        for (let newOption of newOptions) {   
          let newOptions: any = []
          for (let item in filtersState) {
            let splitItem = item.split('|')
            if (splitItem[1] === option.label && splitItem[2] === newOption.label) {
              newOptions = filtersState[item]
            }
          }
          let newGroupOption = {'label': option.label + ' | ' + newOption.label, 'id': undefined, 'options':  newOptions} 
          sortingOptions.push(newGroupOption)
        }
      }
    }
    
    return sortingOptions;
  }, [groupedOptions, treatment, timepoint, sample, categoryAxes, dataAxes, filtersState]);


  const sortingControlGroups: ControlGroup[] = [
    {
      title: 'Sorting Levels',
      controls: [
        { label: 'X-Axis Data', id: 'xAxes', type: 'axis controls', options: sortingGroupedOptions },
      ],
    },
  ];

  const handleSelectDataPipeline = (pipelineId: number) => {
    // localStorage.setItem('selectedPlotId', pipelineId.toString());
    setDataManipulation(pipelineId ? pipelineId : null);
    // onPlotSelect(pipelineId);
  };

  const handleAddPlot = () => {    
    setAddPlot(true);
    setSavePlotModalOpen(true)
  }

  const handleOpenPlotOptionsModal = () => {
    setPlotOptionsModalOpen(true);
    setSelectedPlotTab(1)
  }

  let versionErrorText = ''
  let versionError = false 
  if (versionType.length === 0) {
    versionErrorText = 'Select at least one analysis type'
    versionError = true 
  } 

  const MemoizedSearchableDropdown = React.memo(({ label, value, setValue, options, placeholder, colorDict, helpText, error }: any) => (
    <SearchableDropdown
      label={label}
      value={value}
      setValue={setValue}
      options={options}
      placeholder={placeholder}
      colorDict={colorDict}
      helpText={helpText}
      error={error}
    />
  ));

  const MemoizedSortableCard = React.memo(({ children, title, tileState, handleTile, onSort }: any) => (
    <SortableCard
      showTile={showTile}
      onTile={handleTile}
      tile={tileState}
      title={title}
      onSort={onSort}
    >
      {children}
    </SortableCard>
  ));
  
  const renderControls = 
    <Container sx={{paddingLeft: "0px !important", paddingRight: '0px !important', paddingBottom: '50px', maxHeight: '95vh', overflow: 'auto'}}>
{/*       
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem' }}>
                
          <MemoizedSearchableDropdown
            label="Study"
            value={study}
            setValue={(newValue: SearchableDropdownOption[]) => { handleStudyChange(newValue); setStudy(newValue); }}
            options={Object.keys(studyDict).map(key => ({ id: Number(key), label: studyDict[key], value: studyDict[key] }))}
            placeholder='Studies'
            colorDict={studiesColorDict}
          />

          {/* <MemoizedSearchableDropdown
            label="Samples"
            value={tempSample}
            setValue={setTempSample}
            helpText={sampleErrorText}
            error={sampleError}
            options={Object.keys(tumorDict).map(key => ({ id: Number(key), label: tumorDict[key], value: tumorDict[key] }))}
            placeholder='Samples'
            colorDict={samplesColorDict}
          /> */}
          
          {/* {selectedPlotTab == 1 &&
            <div>
              <MemoizedSearchableDropdown
                label="Analysis Types"
                value={versionType}
                helpText={versionErrorText}
                error={versionError}
                setValue={setVersionType}
                options={Object.keys(versionTypeDict).map(key => ({ id: Number(key), label: versionTypeDict[key], value: versionTypeDict[key] }))}
                placeholder='Analysis Types'
              />
            </div>
          
          } 
        </Box> */}

        

 
      {selectedTab === 1 && (
        <div>
          <Box sx={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
            <Card sx={{paddingBottom: 0, marginBottom: 0, paddingTop: '0rem'}} elevation={0}>
                 {accountOptions.length > 1 ? <SortableCard
                  title="Accounts"
                  showSort={false}
                >
                  <SearchableDropdown 
                    label="Accounts" 
                    value={account} 
                    setValue={setAccount}
                    options={accountOptions}
                    multiple={false}
                  />
                </SortableCard> : <></>}
                <SortableCard
                  title="Partitions"
                  showSort={false}
                  showAddButton
                  onAdd={() => setPartitionModalOpen(true)}
                  showDeleteButton
                  onDelete={() => setConfirmDeletePartitionOpen(true)}
                  onSort={(sortOption: any) => handleBasicSort('Partitions', sortOption)} 
                >
                  <SearchableDropdown 
                    label="Partitions" 
                    value={partition} 
                    setValue={setPartition}
                    options={partitionOptions}
                    multiple={false}
                  />
                </SortableCard>

                {partition.length > 0 && <SortableCard
                  customButton={<IconButton aria-label="" onClick={() => setSampleGroupModalOpen(true)}> 
                                  <SettingsIcon sx={{fontSize: 'small', color: 'grey'}}/>
                                </IconButton>}
                  title="Sample Groups"
                  onTile={() => handleTile('Sample Group|')}
                  tile={tileState['Sample Group|']}
                  showTile={showTile}
                  onSort={(sortOption: any) => handleBasicSort('Sample Groups', sortOption)} 
                >
                  <SearchableDropdown 
                    label="Sample Groups" 
                    value={sampleGroup} 
                    setValue={setSampleGroup}
                    colorDict={sampleGroupColorDict}
                    options={sampleGroupOptions}
                  />
                </SortableCard>}

                <SortableCard
                  showTile={showTile}
                  showAddButton
                  showApplyButton
                  applyDisabled={applySampleDisabled}
                  onApply={() => handleDropdownApply()}
                  onAdd={handleAddSamples}
                  onTile={() => handleTile('Sample Title|')}
                  tile={tileState['Sample Title|']}
                  title="Samples"
                  onSort={(sortOption: any) => handleBasicSort('Sample Title', sortOption)} 
                >
                  <SearchableDropdown 
                    label="Samples" 
                    value={tempSample} 
                    setValue={setTempSample}
                    colorDict={sampleColorDict}
                    options={Object.keys(tumorDict)
                      .map(key => {
                        const value = tumorDict[key];
                        return {
                          id: Number(key),
                          label: value,
                          value: value,
                        };
                      })
                      .sort((a, b) => b.id - a.id)}
                  />
                </SortableCard>

                <SortableCard
                  showTile={showTile}
                  onTile={() => handleTile('Treatment_and_Dose|')}
                  tile={tileState['Treatment_and_Dose|']}
                  title="Treatments"
                  onSort={(sortOption: any) => handleBasicSort('Treatment_and_Dose', sortOption)} 
                >
                  <SearchableDropdown 
                    label="Treatment" 
                    value={treatment} 
                    setValue={setTreatment}
                    options={treatmentOptions}
                  />
                </SortableCard>

                <SortableCard
                  showTile={showTile}
                  onTile={() => handleTile('Timepoint|')}
                  tile={tileState['Timepoint|']}
                  title="Timepoints"
                  onSort={(sortOption: any) => handleBasicSort('Timepoint', sortOption)} 
                >
                  <SearchableDropdown 
                    label="Timepoint" 
                    value={timepoint} 
                    setValue={setTimepoint} 
                    options={timepointOptions}
                  />
                  </SortableCard>

                  {getAxisFilters}
                
            </Card>
          </Box>

          {/*<Box sx={{ padding: '1rem' }}>
            <Accordion
              elevation={1}
              sx={{
                marginBottom: '1rem',
                borderRadius: '8px',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-advanced-content"
                id="panel-advanced-header"
              >
                <Typography variant="subtitle1"><b>Advanced</b></Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                <SortLevels controlGroups={sortingControlGroups} tableItems={advancedSortingState} />
              </AccordionDetails>
            </Accordion>
          </Box>*/}
        </div>
      )}

      {/* {selectedTab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem' }}>
           <DataGrid
              rows={Object.keys(dataPipelineDict).map((p_id) => (
                { id: p_id, name: dataPipelineDict[p_id]['name'] }
              ))}
              columns={[
                // { field: 'id', headerName: 'ID', width: 20 },
                { field: 'name', headerName: 'Pipeline Name', width: 250 },
              ]}
              // autoPageSize
              autoHeight             // Auto adjust height
              hideFooter             // Hide footer info
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              density="compact"
              columnHeaderHeight={0}
              slots={{
                toolbar: CustomToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                },
              }}
              onRowClick={(params) => {handleSelectDataPipeline(params.row.id);}}
              rowSelectionModel={dataManipulation !== null ? [dataManipulation] : []}
            />        
        </Box>
      )}
  
      {selectedTab !== 0 && selectedTab !== 1 && selectedTab !== 2 && (
        <Typography variant="h6" sx={{ padding: '1rem' }}>
          Placeholder for content based on the selected tab
        </Typography>
      )} */}
    </Container>
  ;



return (
      <Grid container direction="row" sx={{overflowY: 'hidden', position: 'fixed'}} >
        {!drawerOpen && <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerOpen}
          sx={{ marginLeft: '.2rem', marginTop: '.3rem', zIndex: 1300, position: 'fixed', left: drawerOpen ? '405px' : '5px', transition: 'left 0.3s ease-out' }}
        >
          <MenuIcon />
        </IconButton>}
        <FilterDrawer
          drawerOpen={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          handleRefresh={handleRefresh}
          selectedTab={selectedTab}
          handleChange={() => {}}
        >
          {renderControls}
        </FilterDrawer>
      
        <Grid item sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative'}}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            <Toolbar 
                className='toolbar' 
                variant="dense"
                sx={{
                    top: 0, 
                    zIndex: 999, 
                    backgroundColor: '#fff', 
                    borderBottom: '.5px solid lightgrey'
                }}
            >
              <Box  sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', width: '100px', }}>
                <IconButton
                  onClick={handleSinglePlot}
                >
                  <CropSquareIcon fontSize="medium" sx={{ color: 'lightgray' }}/>
                </IconButton>

                <IconButton
                  onClick={handleSplitVertical}
                >
                  <SplitscreenIcon fontSize="medium" sx={{ color: 'lightgray' }}/>
                </IconButton>

                <IconButton
                  onClick={handleSplitHorizontal}
                >
                  <SplitscreenIcon fontSize="medium" sx={{ transform: 'rotate(90deg)', color: 'lightgray' }}/>
                </IconButton>
                </Box>
              </Box>
            </Toolbar>

            {/* <TextField
              id="standard-name"
              label={title}
              margin="normal"
              variant="standard"
              className="textfield"
              style={{ 'width': '500px', marginLeft: '2.2rem', marginTop: '0rem' }}
            />
            <Box style={{ marginLeft: 'auto' }}>
              <IconButton
                edge="end"
                aria-label="add"
                onClick={handleClick}
              >
                <AddCircleOutlineIcon fontSize="medium"/>
              </IconButton>

                <IconButton
                  edge="end"
                  aria-label="save"
                  style={{ marginLeft: '1rem' }} // Adjust as needed
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <SaveIcon color='primary' fontSize="medium"/>
                </IconButton>
              </Box> */}

                {/* <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  {actions.map((action) => (
                    <MenuItem onClick={() => {handleAction(action.name as ContentType); handleClose();}} key={action.name}>
                      {action.icon}
                      {action.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Toolbar> */}

              <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', paddingTop: '5px', paddingBottom: '5px'}}>
                {sampleError && 
                  <Box sx={{display: 'flex', justifyContent: 'center', height: '100vh', alignItems: 'center'}}>
                    <Typography variant='body2'>
                      {sampleErrorText}
                    </Typography>
                  </Box>
                }
                <Box style={{maxHeight: '100vh', overflowY: 'auto', paddingBottom: 0, paddingTop: 0}}>
                  <PlotGrid 
                    multiPlotData={plots}
                    currentPage={currentPage}
                    unstagedData={unstagedData}
                    isHorizontalLayout={isHorizontalLayout}
                    layout={plotLayoutFinal}
                    layers={plotLayersFinal}
                    isError={isDataError || tooManyDataPointsError || plotError || selectedPlotIds.length === 0 || selectedVersionTypes.length === 0 ? true : false}
                    alerts={plotAlerts}
                    errorMessage={errorMessage}
                    loadingMessage={loadingMessage}
                    isLoading={isLoading}
                    loadingProgress={dfProgress}
                    numColumns={numTileColumns}
                    tileModeOn={tileModeOn}
                    onAddColumn={tileModeOn ? handleAddTileColumn : undefined}
                    onRemoveColumn={tileModeOn ? handleRemoveTileColumn : undefined}
                    tileSharedY={tileSharedY}
                    setTileSharedY={setTileSharedY}
                    tileSharedX={tileSharedX}
                    setTileSharedX={setTileSharedX}
                    setPlotConfigs={handleSetPlotConfigs}
                    plotConfig={plotConfigs['plot1']}
                    selectedItems={tableItems.selectedItems}
                    setItems={tableItems.setItems}
                    setIsHorizontalLayout={setIsHorizontalLayout} 
                    handleDelete={handleDelete}
                    handleContentChange={handleContentChange}
                    selectedMultiPlot={selectedMultiPlot}
                    setSelectedMultiPlot={setSelectedMultiPlot}
                    openPlotOptionsModal={handleOpenPlotOptionsModal}
                    categoricalColumns={loadedData.categoricalColumns}
                    numericColumns={loadedData.numericColumns}
                    updateFilters={updateFilters}
                    axisFiltersState={axisFiltersState}
                    setAxisFiltersState={setAxisFiltersState}
                    setConfigureTab={()=>setSelectedPlotTab(1)}
                    analyses={filteredAnalyses}
                    imageViewerOpen={imageViewerOpen}
                    setImageViewerOpen={setImageViewerOpen}
                    setDataPipeline={setDataManipulation}
                    setPlotRendering={setPlotRendering}
                  />
                </Box>
            </Box>
          </Box>
          {/* <Box style={{position: 'fixed', height: '90px', bottom: 0, width: '100%', zIndex: 999, backgroundColor: '#fff'}}>
            <ThumbnailPreview 
              thumbnails={thumbnails}
              currentPage={currentPage}
              onClickPage={handlePageChange}
              onAddPage={handleAddPage}
            />
          </Box> */}
          {/* <Box style={{position: 'fixed', height: '300px', bottom: 0, width: '100%', zIndex: 999, backgroundColor: '#fff'}}>
            <ChannelImageViewer/>
          </Box> */}
        </Grid>
        <Grid item xs={3} style={{marginTop: 0, borderLeft: '1px solid #ccc', overflowY: 'hidden'}}>
          <Box>
            <Tabs value={selectedPlotTab} onChange={handleTabChange}>
              <Tab label="Browse" />
              <Tab label="Configure" />
              {/* { userRoles.includes('xsphera') && <Tab label="Xsphera AI" /> } */}
            </Tabs>

          <Container sx={{maxHeight: '80vh', overflow: 'none', paddingLeft: '0px !important', paddingRight: '0px !important'}}>
            <AddPlotGroupModal 
              open={addPlotGroupModalOpen} 
              onClose={handleCloseAddPlotGroupModal} 
              onSave={addNewPlotGroup} 
            />
            <AddPartitionModal 
              partitionOptions={partitionOptions}
              studyOptions={Object.keys(studyDict).map(key => ({ id: Number(key), label: studyDict[key], value: studyDict[key] }))}
              open={partitionModalOpen} 
              onClose={() => setPartitionModalOpen(false)} 
              onSave={addNewPartition} 
            />
            <AddSamplesModal
              open={addSamplesModalOpen}
              studyOptions={Object.keys(studyDict).map(key => ({ id: Number(key), label: studyDict[key], value: studyDict[key] }))}
              onApply={handleApplyStudyFilter}
              onClose={() => setAddSamplesModalOpen(false)}
            />
            <AddFromGroupModal
              open={addGeneModelOpenNano}
              itemName="Genes"
              groupName="Pathways"
              groupOptions={Object.keys(pathwayDict).map(key => ({ id: Number(key), label: pathwayDict[Number(key)].name, value: pathwayDict[Number(key)].name }))}
              onApply={(pathways) => {
                handleAddPathways(pathways.map(p => p.id as number), 'RNA 1.0'); 
                setAddGeneModelOpenNano(false);
              }}
              onClose={() => setAddGeneModelOpenNano(false)}
            />
            <AddFromGroupModal
              open={addGeneModelOpenSeq}
              itemName="Genes"
              groupName="Pathways"
              groupOptions={Object.keys(pathwayDict).map(key => ({ id: Number(key), label: pathwayDict[Number(key)].name, value: pathwayDict[Number(key)].name }))}
              onApply={(pathways) => {
                handleAddPathways(pathways.map(p => p.id as number), 'RNA-Seq 1.0');
                setAddGeneModelOpenSeq(false);
              }}
              onClose={() => setAddGeneModelOpenSeq(false)}
            />
            <AddFromGroupModal
              open={pathwayGroupAddModalVersionType !== undefined}
              itemName="Pathways"
              groupName="Pathway Groups"
              multi={false}
              groupOptions={[{'label': 'Nanostring', 'value': 'nano'}, {'label': 'MSigDB', 'value': 'msig'}, {'label': 'User Defined', 'value': 'user'}, {'label': 'Cell Types', 'value': 'cell'} ]}
              onApply={(pathwayGroup) => {
                const pathwaysToAdd = Object.entries(pathwayDict).filter(([k, v]) => {
                  if (pathwayGroup.length > 0) {
                    if (pathwayGroup[0].value === 'user') {
                      return v.public === false;
                      // return !v.name.includes('Nanostring:') && !v.name.includes('MSigDB:');
                    }
                    else if (pathwayGroup[0].value === 'nano') {
                      return v.name.includes('NS:') && !v.name.includes('All');
                    }
                    else if (pathwayGroup[0].value === 'msig') {
                      return v.name.includes('MSigDB:');
                    }
                    else if (pathwayGroup[0].value === 'cell') {
                      if (v.name.includes(':')){
                        return RNA_CELL_TYPES.includes(v.name.split(':')[1].trim())
                      }
                    }
                  }
                  return false;
                }).map(([k, v]) => Number(k));

                if (pathwaysToAdd.length > 0 && pathwayGroupAddModalVersionType !== undefined) {
                  setAndAddPathways(pathwaysToAdd.map(pid => ({'id':pid, 'label':pathwayDict[pid].name, 'value':pathwayDict[pid].name})), pathwayGroupAddModalVersionType);
                }
                setPathwayGroupAddModalVersionType(undefined);
              }}
              onClose={() => setPathwayGroupAddModalVersionType(undefined)}
            />
            <PlotOptionsModal
                plotConfig={plotConfigs['plot1']}
                plotLayers={plotLayers ? plotLayers : []}
                setPlotConfigs={handleSetPlotConfigs}
                isOpen={plotOptionsModalOpen} 
                onClose={() => setPlotOptionsModalOpen(false)}
                disableSave={disableSave}
                defaultConfig={initialPlotConfig}
                plotTypeOptions={plotTypeOptions}
            />
            <SampleGroupModal
                open={sampleGroupModalOpen}
                onClose={() => setSampleGroupModalOpen(false)}
                onSave={(newSampleGroups: any, sampleGroupColors: any) => {handleSaveSampleGroup(newSampleGroups, sampleGroupColors); setSampleGroupModalOpen(false)}}
                availableSamples={partitionSampleOptions}
                sampleGroupToSampleDict={sampleGroupToSampleDict}
                sampleGroupOptions={sampleGroupOptions}
                tumorDict={tumorDict}
                initialColors={sampleGroupColorDict}
            />
            <GroupEditorModal
                title={'Pathway Editor'}
                open={pathwayEditModalOpenNano}
                isLoading={pathwaysIsLoading}
                groupName="Pathway"
                valueName="Gene"
                onClose={() => {
                  setDefaultOpenPathwayIdNano(undefined);
                  setPathwayEditModalOpenNano(false);
                }}
                onSave={(id: number, newGeneGroups: Groups) => {handleSaveGeneGroup(id, newGeneGroups); }}
                onCreate={handleCreateEmptyGeneGroup}
                onDelete={handleDeleteGeneGroup}
                availableItems={customFilterOptions['RNA 1.0|Gene'] !== undefined ? customFilterOptions['RNA 1.0|Gene'].map((gene: any) => gene.value) : []}
                groupOptions={pathwayDict}
                defaultOpenId={defaultOpenPathwayIdNano}
                idSortOrder={
                    Object.entries(pathwayDict).sort(([, a], [, b]) => {
                        if (a.public === b.public) {   
                            if (a.readonly === b.readonly) {
                                return 0;
                            }
                            return a.readonly ? 1 : -1;
                        }
                        return a.public ? 1 : -1;
                  }).map(([k]) => Number(k))}
            />
            <GroupEditorModal 
                title={'Pathway Editor'}
                open={pathwayEditModalOpenSeq}
                isLoading={pathwaysIsLoading}
                groupName="Pathway"
                valueName="Gene"
                onClose={() => {
                  setDefaultOpenPathwayIdSeq(undefined);
                  setPathwayEditModalOpenSeq(false);
                }}
                onSave={(id:number, newGeneGroups: Groups) => {handleSaveGeneGroup(id, newGeneGroups); }}
                onCreate={handleCreateEmptyGeneGroup}
                onDelete={handleDeleteGeneGroup}
                availableItems={customFilterOptions['RNA-Seq 1.0|Gene'] !== undefined ? customFilterOptions['RNA-Seq 1.0|Gene'].map((gene: any) => gene.value) : []}
                groupOptions={pathwayDict}
                defaultOpenId={defaultOpenPathwayIdSeq}
                idSortOrder={
                    Object.entries(pathwayDict).sort(([, a], [, b]) => {
                      if (a.public === b.public) {   
                          if (a.readonly === b.readonly) {
                              return 0;
                          }
                          return a.readonly ? 1 : -1;
                      }
                      return a.public ? 1 : -1;
                }).map(([k]) => Number(k))}
            />
            <ConfirmationDialog
                  open={confirmDeletePlotGroupOpen}
                  onClose={handleCloseConfirmPlotGroupDeleteDialog}
                  onConfirm={handleConfirmDeletePlotGroup}
                  title="Delete Plot Group"
                  content="Are you sure you want to delete this plot group?"
            />
            <ConfirmationDialog
                  open={confirmDeletePartitionOpen}
                  onClose={() => setConfirmDeletePartitionOpen(false)}
                  onConfirm={handleDeleteCurrentPartition}
                  title="Delete Partition"
                  content="Are you sure you want to delete this Partition?"
            />
            <ConfirmationDialog
                  open={confirmDeletePlotOpen}
                  onClose={handleCloseConfirmPlotDeleteDialog}
                  onConfirm={handleConfirmDeletePlot}
                  title="Delete Plot"
                  content="Are you sure you want to delete this plot?"
            />
            <RenameDialog
              open={renameDialogOpen}
              onClose={() => setRenameDialogOpen(false)}
              onSubmit={(name: string) => handleSubmitRenamePlot(name)}
              defaultValue={renameTargetId !== null ? flatPlotDict[renameTargetId].name : undefined}
              title={"Rename Plot"}
              placeholderText="New Plot Name"
            />
            {/* // Conditionally render only if API calls have completed */}
            {selectedPlotTab === 0 && Object.keys(timepointDict).length > 0 && (
              <BrowseTab
                onPlotSelect={handlePlotSelection}
                plotGroups={plotGroups}
                onAddPlotGroup={handleAddPlotGroup}
                onAddPlot={handleAddPlot}
                onDeletePlotGroup={handleRequestDeletePlotGroup}
                onDeletePlot={handleRequestDeletePlot}
                configurePlot={() => setSelectedPlotTab(1)}
                onRenamePlot={handleRenamePlot}
                openPlotOptionsModal={handleOpenPlotOptionsModal}
                savePlot={handleSavePlot}
                selectedPlots={selectedPlotIds}
                updateSelectedPlots={setSelectedPlotIds}
                disableSave={disableSave}
                isCompareMode={isCompareMode}
                setIsCompareMode={setIsCompareMode}
                versionTypes={versionTypes}
                imageViewerIsOpen={imageViewerOpen}
                toggleImageViewer={() => setImageViewerOpen(!imageViewerOpen)}
              />
            )}
            
            <SavePlotModal 
              open={savePlotModalOpen}
              onClose={() => setSavePlotModalOpen(false)}
              plotGroups={plotGroups} 
              addPlot={addPlot}
              onSave={handleAddNewPlot}
              onSaveAs={handleSaveAsPlot}
              // defaultPlotGroupValue={currentPlotGroup}
              defaultPlotGroupValue={defaultPlotGroup}
              isSuperuser={isSuperuser}
            />

            {selectedPlotTab === 1 && (
              Object.keys(multiPlotData).length != 0 || true ?
                <>
                <Box sx={{ alignSelf: 'flex-end', paddingTop: '25px', paddingLeft: '10px', paddingRight: '10px' }}>
                  <MemoizedSearchableDropdown
                    label="Analysis Types"
                    value={versionType}
                    helpText={versionErrorText}
                    error={versionError}
                    setValue={setVersionType}
                    options={versionTypeOptions}
                    placeholder="Analysis Types"
                  />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <PlotControls
                    controlGroups={controlGroupsData}
                    tableItems={tableItems}
                    plotConfig={plotConfigs.plot1}
                    plotName={currentPlotName}
                    updateFilters={updateFilters}
                    showGrouped={partition.length > 0 && sampleGroup.length > 0}
                    groupedSamples={groupedSampleMode}
                    onGroupSamples={() => setGroupedeSampleMode(!groupedSampleMode)}
                    openPlotOptionsModal={handleOpenPlotOptionsModal}
                    handlePlotNameChange={(name) => setCurrentPlotName(name)}
                    setDataPipeline={setDataManipulation}
                    categoricalColumns={loadedData.categoricalColumns}
                    updatePlotConfig={(config) => {
                      updatePlotConfigWithHeatmapProtection('plot1', config);
                    }}
                    savePlot={(saveAs) => {
                      if (saveAs) {
                        setCurrentPlotGroup(null);
                        setSavePlotModalOpen(true);
                      } else {
                        handleSavePlot();
                      }
                    }}
                    saveSuccess={saveSuccess}
                    //disableSave={false}
                    disableSave={disableSave}
                    availableColumns={loadedData.columns ? loadedData.columns : {}}
                  />
                </Box>
              </> :
              <>
              <div style={{ display: 'flex', height: '200px', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ 
                    textTransform: 'uppercase', 
                    color: '#808080', // This is a medium grey, adjust as needed
                    fontSize: '1rem', // Adjust font size as desired
                    fontWeight: '' // If you want the text to be bold
                  }}>
                    Please Select Only One Plot To Edit
                  </p>
                </div>
              </div>
              </>

            )}

            {selectedPlotTab === 2 && (
              <Assistant
                setGenesNS={(newValues)=> handleSetAxisFiltersState(Object.keys(axisFiltersState).find((key: any) => ((axisFiltersState[key].column === "Gene") && (axisFiltersState[key].versionType === "RNA 1.0")) ),newValues, 2)}
                geneOptionsNS={customFilterOptions['RNA 1.0|Gene']}
                setGenesRNASEQ={(newValues)=> handleSetAxisFiltersState(Object.keys(axisFiltersState).find((key: any) => ((axisFiltersState[key].column === "Gene") && (axisFiltersState[key].versionType === "RNA-Seq 1.0")) ),newValues, 2)}
                geneOptionsRNASEQ={customFilterOptions['RNA-Seq 1.0|Gene']}
                plots={plots}
                plotConfigs={plotConfigs}
                updatePlotConfig={updatePlotConfig}
                setPlots={setPlots}
                selectedMultiPlot={selectedMultiPlot}
                handleResetPlot={handleResetPlot}
                treatmentOptions={treatmentOptions}
                setTreatment={setTreatment}
                timepointOptions={timepointOptions}
                setTimepoint={setTimepoint}
              />
            )}

          </Container>
          </Box>
        </Grid>  
      </Grid>
  );    
}

export default Results;